import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line, Legend } from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || 'http://95.179.207.212:8080';
const WS_BASE = API_BASE.replace('/api', '');

// FlashValue 컴포넌트
const FlashValue: React.FC<{ value: number | null | undefined; format: (v: number) => string; color?: string }> = ({ value, format, color }) => {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef(value);
  useEffect(() => {
    if (prevRef.current !== value && value != null) {
      setFlash(true);
      setTimeout(() => setFlash(false), 600);
    }
    prevRef.current = value;
  }, [value]);
  if (value == null) return <span style={{ color: 'rgba(255,255,255,0.2)', fontFamily: 'monospace' }}>-</span>;
  return (
    <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, transition: 'color 0.2s', color: flash ? '#ffe066' : (color || 'rgba(255,255,255,0.8)') }}>
      {format(value)}
    </span>
  );
};

const fmt = (v: number) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pnlColor = (v: number) => v >= 0 ? '#2ed573' : '#ff6b6b';
const formatHold = (sec: number | null | undefined) => {
  if (!sec || sec < 1) return '-';
  const s = Math.floor(sec);
  if (s < 60) return `${s}초`;
  if (s < 3600) return `${Math.floor(s / 60)}분 ${s % 60}초`;
  if (s < 86400) return `${Math.floor(s / 3600)}시간 ${Math.floor((s % 3600) / 60)}분`;
  return `${Math.floor(s / 86400)}일 ${Math.floor((s % 86400) / 3600)}시간`;
};
const winRateColor = (r: string | null | undefined) => {
  if (!r || r === '-') return 'rgba(255,255,255,0.3)';
  const n = parseFloat(r);
  if (isNaN(n)) return 'rgba(255,255,255,0.3)';
  if (n >= 60) return '#2ed573';
  if (n >= 40) return '#f7971e';
  return '#ff6b6b';
};

const PointRulePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // URL path에서 현재 탭 추출
  const getTabFromPath = (): 'rules' | 'symbols' | 'accounts' | 'statistics' => {
    const p = location.pathname;
    if (p === '/point-rule/symbols') return 'symbols';
    if (p === '/point-rule/accounts') return 'accounts';
    if (p === '/point-rule/statistics') return 'statistics';
    return 'rules';
  };

  const [tab, setTab] = useState<'rules' | 'symbols' | 'accounts' | 'statistics'>(getTabFromPath());

  // URL 변경 시 tab 동기화
  useEffect(() => {
    const t = getTabFromPath();
    if (t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // tab 변경 시 URL 동기화
  const switchTab = (newTab: 'rules' | 'symbols' | 'accounts' | 'statistics') => {
    setTab(newTab);
    const url = `/point-rule/${newTab}`;
    if (location.pathname !== url) navigate(url);
  };

  // ── 룰 관리 상태 ──
  const [rules, setRules] = useState<any[]>([]);
  const [ruleShowInactive, setRuleShowInactive] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [ruleForm, setRuleForm] = useState({
    name: '', description: '', masterAccountId: '', defaultPoint: 100,
    forceEntryEnabled: false, defaultTimeoutSeconds: 300, defaultAdversePoint: 50
  });
  const [symbolPoints, setSymbolPoints] = useState<Record<string, number | ''>>({});
  const [symbolTimeouts, setSymbolTimeouts] = useState<Record<string, number | ''>>({});
  const [symbolAdversePoints, setSymbolAdversePoints] = useState<Record<string, number | ''>>({});
  const [symbols, setSymbols] = useState<any[]>([]);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [symbolGroupFilter, setSymbolGroupFilter] = useState('ALL');

  // ── 계좌 관리 상태 ──
  const [accounts, setAccounts] = useState<any[]>([]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [newAccount, setNewAccount] = useState({ accountId: '', name: '', ruleId: '' });
  // 그룹 검색
  const [groupList, setGroupList] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupAccounts, setGroupAccounts] = useState<any[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerResult, setRegisterResult] = useState('');

  // ── 통계 상태 ──
  const [statistics, setStatistics] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [accountDetail, setAccountDetail] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  // 실시간 데이터
  const [equityMap, setEquityMap] = useState<Record<string, any>>({});
  const [floatingMap, setFloatingMap] = useState<Record<string, any>>({});
  const [flashFloating, setFlashFloating] = useState<Record<string, boolean>>({});

  // 계좌 탭 - 필터 + 다중선택
  const [accountFilterRule, setAccountFilterRule] = useState<string>('ALL');
  const [accountSearch, setAccountSearch] = useState<string>('');
  const [accountCheckedIds, setAccountCheckedIds] = useState<Set<string>>(new Set());

  // 통계 탭 - 공통 필터 + 서브탭
  const [statsSubTab, setStatsSubTab] = useState<'accounts' | 'symbols' | 'daily' | 'monthly'>('accounts');
  const [statsRuleId, setStatsRuleId] = useState<string>('ALL');
  const [statsFrom, setStatsFrom] = useState<string>('');
  const [statsTo, setStatsTo] = useState<string>('');
  const [statsStatus, setStatsStatus] = useState<string>('ALL');

  // 심볼 설정 탭 (룰별 심볼 포인트 관리)
  const [selSymbolRuleId, setSelSymbolRuleId] = useState<string>('');
  const [selRuleDetail, setSelRuleDetail] = useState<any>(null); // 선택된 룰 정보
  const [symbolPointsEdit, setSymbolPointsEdit] = useState<Record<string, number | ''>>({}); // 현재 편집중
  const [symbolPointsOriginal, setSymbolPointsOriginal] = useState<Record<string, number | ''>>({}); // 원본 (변경 감지용)
  // 심볼별 강제 진입 시간/포인트 강제진입 (NULL=룰 기본값 사용)
  const [symbolTimeoutsEdit, setSymbolTimeoutsEdit] = useState<Record<string, number | ''>>({});
  const [symbolTimeoutsOriginal, setSymbolTimeoutsOriginal] = useState<Record<string, number | ''>>({});
  const [symbolAdverseEdit, setSymbolAdverseEdit] = useState<Record<string, number | ''>>({});
  const [symbolAdverseOriginal, setSymbolAdverseOriginal] = useState<Record<string, number | ''>>({});
  const [symbolTabSearch, setSymbolTabSearch] = useState('');
  const [symbolTabGroupFilter, setSymbolTabGroupFilter] = useState('ALL');
  const [symbolTabShowChangedOnly, setSymbolTabShowChangedOnly] = useState(false);
  const [collapsedSymbolGroups, setCollapsedSymbolGroups] = useState<Set<string>>(new Set());
  const [bulkEditValue, setBulkEditValue] = useState<string>(''); // 일괄 설정용
  const [savingSymbols, setSavingSymbols] = useState(false);

  // 통화별 통계
  const [symbolStats, setSymbolStats] = useState<any>({ groups: [], total: {} });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // 일별/월별 통계
  const [dailyStats, setDailyStats] = useState<any>({ rows: [], total: {} });
  const [monthlyStats, setMonthlyStats] = useState<any>({ rows: [], total: {} });

  useEffect(() => {
    if (tab === 'rules') { fetchRules(ruleShowInactive); fetchSymbols(); }
    else if (tab === 'symbols') { fetchRules(false); fetchSymbols(); }
    else if (tab === 'accounts') { fetchAccounts(); fetchRules(false); fetchGroups(); fetchStatistics(); }
    else if (tab === 'statistics') {
      fetchRules(false);
      const f = { ruleId: statsRuleId, from: statsFrom, to: statsTo, status: statsStatus };
      fetchStatistics(f);
      fetchSymbolStats(f);
      fetchDailyStats(f);
      fetchMonthlyStats(f);
    }
  }, [tab, ruleShowInactive]);

  // 심볼 설정 탭에서 룰 선택 시 상세 로드
  useEffect(() => {
    if (tab === 'symbols') {
      if (hasUnsavedChanges() && symbolPointsOriginal &&
          Object.keys(symbolPointsOriginal).length > 0) {
        if (!window.confirm('저장하지 않은 변경사항이 있습니다. 계속하시겠습니까?')) {
          return;
        }
      }
      loadSymbolRuleDetail(selSymbolRuleId);
    }
  }, [selSymbolRuleId]);

  // 통계 필터 변경 시 재조회 (debounce 없이 단순)
  useEffect(() => {
    if (tab === 'statistics') {
      const f = { ruleId: statsRuleId, from: statsFrom, to: statsTo, status: statsStatus };
      fetchStatistics(f);
      fetchSymbolStats(f);
      fetchDailyStats(f);
      fetchMonthlyStats(f);
      // 열려있는 상세도 필터에 맞게 재조회
      if (selectedAccount) {
        fetchAccountDetail(selectedAccount, { status: statsStatus, from: statsFrom, to: statsTo });
      }
    }
  }, [statsRuleId, statsFrom, statsTo, statsStatus]);

  const fetchRules = async (includeInactive = false) => {
    try {
      const res = await api.get('/v1/point-rule/rules', {
        params: includeInactive ? { includeInactive: true } : {}
      });
      setRules(res.data);
    } catch (e) {}
  };

  const hardDeleteRule = async (id: number) => {
    if (!window.confirm('이 룰을 영구 삭제합니다. 되돌릴 수 없습니다. 계속?')) return;
    try {
      await api.delete(`/v1/point-rule/rules/${id}/hard`);
      fetchRules(ruleShowInactive);
    } catch (e: any) {
      alert(e?.response?.data?.error || '삭제 실패');
    }
  };
  const fetchSymbols = async () => {
    try { const res = await api.get('/v1/point-rule/symbols'); setSymbols(res.data); } catch (e) {}
  };
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/v1/point-rule/accounts');
      setAccounts(res.data);
      // DB 캐시값으로 초기값 설정
      const initEquity: Record<string, any> = {};
      res.data.forEach((acc: any) => {
        if (acc.lastBalance != null || acc.lastEquity != null) {
          initEquity[acc.accountId] = {
            balance: acc.lastBalance,
            equity: acc.lastEquity,
            drawdown: acc.lastDrawdown,
            name: acc.name,
          };
        }
      });
      setEquityMap(prev => {
        const merged = { ...initEquity };
        Object.keys(prev).forEach(id => { if (prev[id]) merged[id] = prev[id]; });
        return merged;
      });
    } catch (e) {}
    finally { setLoading(false); }
  };

  // Floating 10초 폴링 (WebSocket 백업/fallback용 - 실시간은 STOMP로)
  useEffect(() => {
    const fetchFloating = () => {
      api.get('/v1/positions/floating').then((res: any) => {
        const newMap: Record<string, any> = {};
        res.data.forEach((p: any) => { newMap[p.account_id] = p; });
        setFloatingMap(prev => {
          // WebSocket으로 받은 값이 더 최신이면 덮지 않음 (has_position 키 존재로 판단)
          const merged = { ...newMap };
          Object.keys(prev).forEach(id => {
            if (prev[id]?.has_position !== undefined) merged[id] = prev[id];
          });
          return merged;
        });
      }).catch(() => {});
    };
    fetchFloating();
    const t = setInterval(fetchFloating, 10000);
    return () => clearInterval(t);
  }, []);

  // WebSocket 실시간 연결
  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE}/api/ws`),
      reconnectDelay: 5000,
      onConnect: () => {
        // 기존 B북 등 MDD 룰용
        client.subscribe('/topic/equity-updates', (msg: any) => {
          const data = JSON.parse(msg.body);
          setEquityMap(prev => ({
            ...prev,
            [data.accountId]: {
              equity: data.equity,
              balance: data.balance,
              drawdown: data.drawdown,
              name: data.name,
            }
          }));
        });

        // 전환 포인트 룰 계좌 전용 - 실시간 Equity/Balance/Floating
        client.subscribe('/topic/point-rule-equity', (msg: any) => {
          const data = JSON.parse(msg.body);
          setEquityMap(prev => ({
            ...prev,
            [data.accountId]: {
              equity: data.equity,
              balance: data.balance,
              name: data.name || prev[data.accountId]?.name,
            }
          }));
          // Floating 값 업데이트 (기존 polling 구조와 호환: {floating_total: n})
          setFloatingMap(prev => {
            const oldVal = Number(prev[data.accountId]?.floating_total);
            const newVal = data.hasPosition ? Number(data.floating) : 0;
            if (prev[data.accountId] !== undefined && oldVal !== newVal) {
              setFlashFloating(f => ({ ...f, [data.accountId]: true }));
              setTimeout(() => setFlashFloating(f => ({ ...f, [data.accountId]: false })), 600);
            }
            return {
              ...prev,
              [data.accountId]: { floating_total: newVal, has_position: data.hasPosition }
            };
          });
        });
      },
    });
    client.activate();
    return () => { client.deactivate(); };
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await api.get('/v1/accounts/groups');
      if (Array.isArray(res.data)) setGroupList(res.data);
    } catch (e) {}
  };

  const searchGroupAccounts = async () => {
    if (!selectedGroup.trim()) return;
    setGroupLoading(true);
    setGroupAccounts([]);
    setCheckedIds(new Set());
    try {
      const res = await api.get('/v1/accounts/groups/accounts', { params: { group: selectedGroup } });
      setGroupAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
    finally { setGroupLoading(false); }
  };

  const toggleCheck = (login: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(login) ? next.delete(login) : next.add(login);
      return next;
    });
  };

  const toggleAll = () => {
    if (checkedIds.size === groupAccounts.length) setCheckedIds(new Set());
    else setCheckedIds(new Set(groupAccounts.map((a: any) => String(a.login))));
  };
  const fetchStatistics = async (filters?: { ruleId?: string; from?: string; to?: string; status?: string }) => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters?.ruleId && filters.ruleId !== 'ALL') params.ruleId = filters.ruleId;
      if (filters?.from) params.from = filters.from;
      if (filters?.to) params.to = filters.to;
      if (filters?.status && filters.status !== 'ALL') params.status = filters.status;
      const res = await api.get('/v1/point-rule/statistics', { params });
      setStatistics(res.data.accounts || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const fetchSymbolStats = async (filters?: { ruleId?: string; from?: string; to?: string; status?: string }) => {
    try {
      const params: any = {};
      if (filters?.ruleId && filters.ruleId !== 'ALL') params.ruleId = filters.ruleId;
      if (filters?.from) params.from = filters.from;
      if (filters?.to) params.to = filters.to;
      if (filters?.status && filters.status !== 'ALL') params.status = filters.status;
      const res = await api.get('/v1/point-rule/statistics/by-symbol', { params });
      setSymbolStats(res.data || { groups: [], total: {} });
    } catch (e) {}
  };

  const fetchDailyStats = async (filters?: { ruleId?: string; from?: string; to?: string; status?: string }) => {
    try {
      const params: any = {};
      if (filters?.ruleId && filters.ruleId !== 'ALL') params.ruleId = filters.ruleId;
      if (filters?.from) params.from = filters.from;
      if (filters?.to) params.to = filters.to;
      if (filters?.status && filters.status !== 'ALL') params.status = filters.status;
      const res = await api.get('/v1/point-rule/statistics/daily', { params });
      setDailyStats(res.data || { rows: [], total: {} });
    } catch (e) {}
  };

  const fetchMonthlyStats = async (filters?: { ruleId?: string; from?: string; to?: string; status?: string }) => {
    try {
      const params: any = {};
      if (filters?.ruleId && filters.ruleId !== 'ALL') params.ruleId = filters.ruleId;
      if (filters?.from) params.from = filters.from;
      if (filters?.to) params.to = filters.to;
      if (filters?.status && filters.status !== 'ALL') params.status = filters.status;
      const res = await api.get('/v1/point-rule/statistics/monthly', { params });
      setMonthlyStats(res.data || { rows: [], total: {} });
    } catch (e) {}
  };

  // ── 룰 모달 열기 ──
  const openCreateRule = () => {
    setEditingRule(null);
    setRuleForm({
      name: '', description: '', masterAccountId: '', defaultPoint: 100,
      forceEntryEnabled: false, defaultTimeoutSeconds: 300, defaultAdversePoint: 50
    });
    setSymbolPoints({});
    setSymbolTimeouts({});
    setSymbolAdversePoints({});
    setShowRuleModal(true);
  };

  const openEditRule = async (rule: any) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      masterAccountId: rule.masterAccountId,
      defaultPoint: rule.defaultPoint,
      forceEntryEnabled: !!rule.forceEntryEnabled,
      defaultTimeoutSeconds: rule.defaultTimeoutSeconds || 300,
      defaultAdversePoint: rule.defaultAdversePoint || 50,
    });
    try {
      const res = await api.get(`/v1/point-rule/rules/${rule.id}`);
      setSymbolPoints(res.data.symbolPoints || {});
      setSymbolTimeouts(res.data.symbolTimeouts || {});
      setSymbolAdversePoints(res.data.symbolAdversePoints || {});
    } catch (e) {
      setSymbolPoints({});
      setSymbolTimeouts({});
      setSymbolAdversePoints({});
    }
    setShowRuleModal(true);
  };

  const saveRule = async () => {
    try {
      const body = {
        ...ruleForm,
        symbolPoints: Object.fromEntries(
          Object.entries(symbolPoints).filter(([, v]) => v !== '' && v !== null)
        ),
        symbolTimeouts: Object.fromEntries(
          Object.entries(symbolTimeouts).filter(([, v]) => v !== '' && v !== null)
        ),
        symbolAdversePoints: Object.fromEntries(
          Object.entries(symbolAdversePoints).filter(([, v]) => v !== '' && v !== null)
        ),
      };
      if (editingRule) {
        await api.put(`/v1/point-rule/rules/${editingRule.id}`, body);
      } else {
        await api.post('/v1/point-rule/rules', body);
      }
      setShowRuleModal(false);
      fetchRules();
    } catch (e: any) { alert(e?.response?.data?.error || '저장 실패'); }
  };

  const deleteRule = async (id: number) => {
    if (!window.confirm('이 룰을 삭제하시겠습니까?')) return;
    try { await api.delete(`/v1/point-rule/rules/${id}`); fetchRules(); } catch (e) {}
  };

  // ── 심볼 설정 탭 ──
  const loadSymbolRuleDetail = async (ruleId: string) => {
    if (!ruleId) {
      setSelRuleDetail(null);
      setSymbolPointsEdit({});
      setSymbolPointsOriginal({});
      setSymbolTimeoutsEdit({});
      setSymbolTimeoutsOriginal({});
      setSymbolAdverseEdit({});
      setSymbolAdverseOriginal({});
      return;
    }
    try {
      const res = await api.get(`/v1/point-rule/rules/${ruleId}`);
      setSelRuleDetail(res.data);
      const sp = res.data.symbolPoints || {};
      const st = res.data.symbolTimeouts || {};
      const sa = res.data.symbolAdversePoints || {};
      setSymbolPointsEdit({ ...sp });
      setSymbolPointsOriginal({ ...sp });
      setSymbolTimeoutsEdit({ ...st });
      setSymbolTimeoutsOriginal({ ...st });
      setSymbolAdverseEdit({ ...sa });
      setSymbolAdverseOriginal({ ...sa });
    } catch (e) {
      setSelRuleDetail(null);
      setSymbolPointsEdit({});
      setSymbolPointsOriginal({});
      setSymbolTimeoutsEdit({});
      setSymbolTimeoutsOriginal({});
      setSymbolAdverseEdit({});
      setSymbolAdverseOriginal({});
    }
  };

  const saveSymbolPoints = async () => {
    if (!selSymbolRuleId || !selRuleDetail) return;
    setSavingSymbols(true);
    try {
      const body = {
        name: selRuleDetail.name,
        description: selRuleDetail.description || '',
        masterAccountId: selRuleDetail.masterAccountId,
        defaultPoint: selRuleDetail.defaultPoint,
        forceEntryEnabled: selRuleDetail.forceEntryEnabled,
        defaultTimeoutSeconds: selRuleDetail.defaultTimeoutSeconds,
        defaultAdversePoint: selRuleDetail.defaultAdversePoint,
        symbolPoints: Object.fromEntries(
          Object.entries(symbolPointsEdit).filter(([, v]) => v !== '' && v !== null)
        ),
        symbolTimeouts: Object.fromEntries(
          Object.entries(symbolTimeoutsEdit).filter(([, v]) => v !== '' && v !== null)
        ),
        symbolAdversePoints: Object.fromEntries(
          Object.entries(symbolAdverseEdit).filter(([, v]) => v !== '' && v !== null)
        ),
      };
      await api.put(`/v1/point-rule/rules/${selSymbolRuleId}`, body);
      setSymbolPointsOriginal({ ...symbolPointsEdit });
      setSymbolTimeoutsOriginal({ ...symbolTimeoutsEdit });
      setSymbolAdverseOriginal({ ...symbolAdverseEdit });
      alert('저장되었습니다');
    } catch (e: any) {
      alert(e?.response?.data?.error || '저장 실패');
    } finally {
      setSavingSymbols(false);
    }
  };

  const hasUnsavedChanges = () => {
    const allKeys = Array.from(new Set([
      ...Object.keys(symbolPointsEdit), ...Object.keys(symbolPointsOriginal),
      ...Object.keys(symbolTimeoutsEdit), ...Object.keys(symbolTimeoutsOriginal),
      ...Object.keys(symbolAdverseEdit), ...Object.keys(symbolAdverseOriginal),
    ]));
    for (const k of allKeys) {
      const aP = symbolPointsEdit[k], bP = symbolPointsOriginal[k];
      const aT = symbolTimeoutsEdit[k], bT = symbolTimeoutsOriginal[k];
      const aA = symbolAdverseEdit[k], bA = symbolAdverseOriginal[k];
      const num = (v: any) => v === '' || v == null ? null : Number(v);
      if (num(aP) !== num(bP)) return true;
      if (num(aT) !== num(bT)) return true;
      if (num(aA) !== num(bA)) return true;
    }
    return false;
  };

  // ── 계좌 등록 ──
  const addAccount = async () => {
    const ids = newAccount.accountId.trim()
      ? [newAccount.accountId.trim()]
      : Array.from(checkedIds);
    if (ids.length === 0) { alert('등록할 계좌를 선택하거나 직접 입력해주세요.'); return; }
    if (!newAccount.ruleId) { alert('룰을 선택해주세요.'); return; }
    setRegisterLoading(true);
    setRegisterResult('');
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        await api.post('/v1/point-rule/accounts', {
          accountId: id,
          ruleId: parseInt(newAccount.ruleId),
        });
        successCount++;
      } catch { failCount++; }
    }
    setRegisterLoading(false);
    if (failCount === 0) {
      setShowAccountModal(false);
      setNewAccount({ accountId: '', name: '', ruleId: '' });
      setCheckedIds(new Set());
      setGroupAccounts([]);
      setRegisterResult('');
      fetchAccounts();
    } else {
      setRegisterResult(`${successCount}개 성공, ${failCount}개 실패 (이미 등록된 계좌 제외)`);
      fetchAccounts();
    }
  };

  const removeAccount = async (accountId: string) => {
    if (!window.confirm(`${accountId} 계좌를 비활성화하시겠습니까?`)) return;
    try { await api.delete(`/v1/point-rule/accounts/${accountId}`); fetchAccounts(); } catch (e) {}
  };

  const fetchAccountDetail = async (accountId: string, filters?: { status?: string; from?: string; to?: string }) => {
    try {
      const params: any = {};
      if (filters?.status && filters.status !== 'ALL') params.status = filters.status;
      if (filters?.from) params.from = filters.from;
      if (filters?.to) params.to = filters.to;
      const res = await api.get(`/v1/point-rule/statistics/${accountId}`, { params });
      setAccountDetail(res.data);
      setSelectedAccount(accountId);
    } catch (e) {}
  };

  // 그룹 목록 추출
  const groups = ['ALL', ...Array.from(new Set(symbols.map((s: any) => s.groupName).filter(Boolean)))];
  const filteredSymbols = symbols.filter(s =>
    (symbolGroupFilter === 'ALL' || s.groupName === symbolGroupFilter) &&
    (s.symbol?.toLowerCase().includes(symbolSearch.toLowerCase()) ||
     s.description?.toLowerCase().includes(symbolSearch.toLowerCase()))
  );

  const statusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return '#f7971e';
      case 'FILLED': return '#2ed573';
      case 'CANCELLED': return 'rgba(255,255,255,0.3)';
      case 'CLOSED': return '#667eea';
      default: return '#fff';
    }
  };
  const statusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return '예약중';
      case 'FILLED': return '체결됨';
      case 'CANCELLED': return '취소됨';
      case 'CLOSED': return '청산완료';
      default: return status;
    }
  };

  // 진입 유형 배지 정보
  const entryTypeBadge = (entryType: string | null | undefined) => {
    switch (entryType) {
      case 'LIMIT_FILLED': return { icon: '🟢', label: 'Limit', color: '#22c55e' };
      case 'FORCED_BY_TIME': return { icon: '⏰', label: '시간 강제', color: '#f59e0b' };
      case 'FORCED_BY_ADVERSE_POINT': return { icon: '⚡', label: '포인트 강제', color: '#ef4444' };
      default: return null;
    }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>📍 전환 포인트</h1>
          <p style={s.sub}>포지션 진입가 기준 포인트 도달 시 마스터 헤지 진입</p>
        </div>
      </div>

      {/* 탭 */}
      <div style={s.tabs}>
        {[
          { key: 'rules', label: '⚙️ 룰 관리' },
          { key: 'symbols', label: '💱 심볼 설정' },
          { key: 'accounts', label: '📋 전환 포인트 계좌' },
          { key: 'statistics', label: '📊 통계' },
        ].map(t => (
          <button key={t.key} style={tab === t.key ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={() => switchTab(t.key as any)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── 룰 관리 탭 ── */}
      {tab === 'rules' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', fontSize: '13px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={ruleShowInactive}
                onChange={e => setRuleShowInactive(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span>🔴 비활성 룰도 보기</span>
              {ruleShowInactive && (
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                  (비활성 + 활성 계좌 0인 룰만 영구 삭제 가능)
                </span>
              )}
            </label>
            <button style={s.addBtn} onClick={openCreateRule}>+ 룰 생성</button>
          </div>
          <div style={s.tableWrap}>
            {rules.length === 0 ? <div style={s.empty}>생성된 룰이 없습니다</div> : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>상태</th>
                    <th style={s.th}>ID</th>
                    <th style={s.th}>룰 이름</th>
                    <th style={s.th}>마스터 계좌</th>
                    <th style={s.th}>기본 포인트</th>
                    <th style={s.th}>강제 진입</th>
                    <th style={s.th}>심볼 설정</th>
                    <th style={s.th}>적용 계좌</th>
                    <th style={s.th}>생성일</th>
                    <th style={s.th}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r, idx) => {
                    const isInactive = !r.isActive;
                    const zeroAccounts = r.accountCount === 0;
                    const canHardDelete = isInactive && zeroAccounts;
                    return (
                      <tr key={r.id} style={{
                        ...(idx % 2 === 0 ? s.trEven : s.trOdd),
                        ...(isInactive ? { opacity: 0.55 } : {}),
                      }}>
                        <td style={s.td}>
                          {isInactive ? (
                            <span style={{ color: '#ff6b6b', fontSize: '11px', fontWeight: 600 }}>🔴 비활성</span>
                          ) : (
                            <span style={{ color: '#2ed573', fontSize: '11px', fontWeight: 600 }}>🟢 활성</span>
                          )}
                        </td>
                        <td style={s.td}><span style={{ ...s.mono, color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>#{r.id}</span></td>
                        <td style={s.td}>
                          <span style={{ color: isInactive ? 'rgba(255,255,255,0.5)' : '#667eea', fontWeight: 700 }}>{r.name}</span>
                          {!isInactive && zeroAccounts && (
                            <span title="이 룰을 사용하는 활성 계좌가 없습니다" style={{
                              marginLeft: '8px', color: '#ffe066', fontSize: '11px',
                              padding: '2px 6px', borderRadius: '3px',
                              background: 'rgba(255,224,102,0.1)',
                              border: '1px solid rgba(255,224,102,0.3)',
                            }}>⚠ 미사용</span>
                          )}
                        </td>
                        <td style={s.td}><span style={s.mono}>{r.masterAccountId}</span></td>
                        <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{r.defaultPoint} pt</span></td>
                        <td style={s.td}>
                          {r.forceEntryEnabled ? (
                            <span title={`시간 ${r.defaultTimeoutSeconds}초 / 포인트강제 ${r.defaultAdversePoint}pt`} style={{
                              color: '#2ed573', fontSize: '11px', fontWeight: 700,
                              padding: '2px 8px', borderRadius: '4px',
                              background: 'rgba(46,213,115,0.15)',
                              border: '1px solid rgba(46,213,115,0.4)',
                            }}>⚡ ON · {r.defaultTimeoutSeconds}s / {r.defaultAdversePoint}pt</span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                        <td style={s.td}><span style={s.mono}>{r.symbolCount}개</span></td>
                        <td style={s.td}>
                          <span style={{
                            ...s.mono,
                            color: !isInactive && zeroAccounts ? '#ffe066' : undefined,
                            fontWeight: !isInactive && zeroAccounts ? 700 : undefined,
                          }}>{r.accountCount}개</span>
                        </td>
                        <td style={s.td}><span style={{ ...s.mono, fontSize: '11px' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString('ko-KR') : '-'}</span></td>
                        <td style={s.td}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {!isInactive ? (
                              <>
                                <button style={s.editBtn} onClick={() => openEditRule(r)}>수정</button>
                                <button style={s.delBtn} onClick={() => deleteRule(r.id)}>비활성화</button>
                              </>
                            ) : (
                              <button
                                onClick={() => hardDeleteRule(r.id)}
                                disabled={!canHardDelete}
                                title={canHardDelete ? '완전 삭제' : '사용 중인 활성 계좌 있음'}
                                style={{
                                  background: canHardDelete ? 'rgba(255,107,107,0.2)' : 'rgba(255,255,255,0.05)',
                                  border: '1px solid ' + (canHardDelete ? 'rgba(255,107,107,0.5)' : 'rgba(255,255,255,0.15)'),
                                  color: canHardDelete ? '#ff6b6b' : 'rgba(255,255,255,0.3)',
                                  padding: '5px 10px', borderRadius: '4px',
                                  fontSize: '11px', fontWeight: 600,
                                  cursor: canHardDelete ? 'pointer' : 'not-allowed',
                                }}
                              >🗑️ 영구 삭제</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── 심볼 설정 탭 ── */}
      {tab === 'symbols' && (() => {
        // 그룹 이름 통일 (broker suffix 제거)
        const stripSuffix = (g: string) => {
          if (!g) return g;
          const dotIdx = g.indexOf('.');
          return dotIdx > 0 ? g.substring(0, dotIdx) : g;
        };
        // path 기반 그룹 추출 (중간 경로 = 그룹)
        // 예: "Market\Forex\AUDCAD" → "Forex"
        //    "No Service\Energy Market\BRENT" → "Energy Market"
        //    "No Service\Equties synchro\0001.HK" → "Equties synchro"
        const getSymGroup = (sym: any) => {
          const path = sym.path || '';
          if (!path) return '기타';
          // 백슬래시(단일/이중) 또는 슬래시로 split
          const parts = path.split(/[\\\/]+/).filter(Boolean);
          let group: string;
          if (parts.length >= 2) {
            // 마지막은 심볼이므로 그 앞 부분을 그룹으로
            group = parts[parts.length - 2];
          } else {
            group = parts[0] || '기타';
          }
          return stripSuffix(group);
        };

        // 그룹 필터 옵션 (path에서 추출 + suffix 제거)
        const groupOptions = Array.from(new Set(symbols.map((s: any) => getSymGroup(s)).filter(Boolean))).sort();

        // 필터링된 심볼 리스트
        const filtered = symbols.filter((sym: any) => {
          if (symbolTabGroupFilter !== 'ALL' && getSymGroup(sym) !== symbolTabGroupFilter) return false;
          if (symbolTabSearch.trim()) {
            const q = symbolTabSearch.trim().toLowerCase();
            if (!sym.symbol?.toLowerCase().includes(q)
                && !sym.description?.toLowerCase().includes(q)) return false;
          }
          if (symbolTabShowChangedOnly) {
            // "커스텀" = 현재 값(저장된 것 포함)이 존재하고 기본값과 다름
            //   OR 세션 내 변경 (원본과 현재 다름)
            const cur = symbolPointsEdit[sym.symbol];
            const orig = symbolPointsOriginal[sym.symbol];
            const curNum = cur === '' || cur == null ? null : Number(cur);
            const origNum = orig === '' || orig == null ? null : Number(orig);
            const isCustom = curNum != null; // 기본값 아닌 값이 설정됨
            const isSessionChanged = curNum !== origNum; // 세션 내 변경
            if (!isCustom && !isSessionChanged) return false;
          }
          return true;
        });

        const grouped: Record<string, any[]> = {};
        filtered.forEach((sym: any) => {
          const g = getSymGroup(sym);
          if (!grouped[g]) grouped[g] = [];
          grouped[g].push(sym);
        });
        const groupKeys = Object.keys(grouped).sort();

        // 변경 개수
        const changedCount = symbols.filter((sym: any) => {
          const cur = symbolPointsEdit[sym.symbol];
          const orig = symbolPointsOriginal[sym.symbol];
          const curNum = cur === '' || cur == null ? null : Number(cur);
          const origNum = orig === '' || orig == null ? null : Number(orig);
          return curNum !== origNum;
        }).length;

        // 룰 미선택 상태
        if (!selSymbolRuleId) {
          return (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px',
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px',
              }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontWeight: 600 }}>📍 편집할 룰 선택:</span>
                <select
                  value={selSymbolRuleId}
                  onChange={e => setSelSymbolRuleId(e.target.value)}
                  style={{
                    background: '#1a1a2e',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '6px', color: '#fff',
                    padding: '8px 12px', fontSize: '13px',
                    minWidth: '220px', cursor: 'pointer',
                    colorScheme: 'dark',
                  }}
                >
                  <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>-- 룰을 선택하세요 --</option>
                  {rules.map(r => (
                    <option key={r.id} value={String(r.id)} style={{ background: '#1a1a2e', color: '#fff' }}>
                      📍 {r.name} · ID {r.id} · 기본 {r.defaultPoint}pt · 계좌 {r.accountCount}개
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', fontSize: '14px' }}>
                💡 편집하려는 룰을 선택하면 심볼별 포인트를 설정할 수 있습니다.
              </div>
            </div>
          );
        }

        // 룰 선택된 상태 - 본격 편집 UI
        const defaultPoint = selRuleDetail?.defaultPoint || 100;
        const unsaved = hasUnsavedChanges();

        return (
          <div>
            {/* 상단 정보 + 액션 바 */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px',
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px', flexWrap: 'wrap',
            }}>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontWeight: 600 }}>📍 편집 중:</span>
              <select
                value={selSymbolRuleId}
                onChange={e => setSelSymbolRuleId(e.target.value)}
                style={{
                  background: '#1a1a2e',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px', color: '#fff',
                  padding: '6px 12px', fontSize: '13px',
                  minWidth: '200px', cursor: 'pointer',
                  colorScheme: 'dark',
                }}
              >
                {rules.map(r => (
                  <option key={r.id} value={String(r.id)} style={{ background: '#1a1a2e', color: '#fff' }}>
                    📍 {r.name} · ID {r.id} · 계좌 {r.accountCount}개
                  </option>
                ))}
              </select>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                기본 포인트: <b style={{ color: '#f7971e' }}>{defaultPoint}pt</b>
              </span>
              {unsaved && (
                <span style={{ color: '#ffe066', fontSize: '12px', fontWeight: 600 }}>
                  🟡 저장되지 않은 변경사항 ({changedCount}개)
                </span>
              )}
              <div style={{ flex: 1 }} />
              <button
                onClick={() => setSymbolPointsEdit({ ...symbolPointsOriginal })}
                disabled={!unsaved}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: unsaved ? '#fff' : 'rgba(255,255,255,0.3)',
                  padding: '8px 16px', borderRadius: '6px',
                  fontSize: '13px', cursor: unsaved ? 'pointer' : 'not-allowed',
                }}
              >↺ 변경 취소</button>
              <button
                onClick={saveSymbolPoints}
                disabled={!unsaved || savingSymbols}
                style={{
                  background: unsaved ? 'rgba(46,213,115,0.2)' : 'rgba(255,255,255,0.05)',
                  border: '1px solid ' + (unsaved ? 'rgba(46,213,115,0.5)' : 'rgba(255,255,255,0.15)'),
                  color: unsaved ? '#2ed573' : 'rgba(255,255,255,0.3)',
                  padding: '8px 20px', borderRadius: '6px',
                  fontSize: '13px', fontWeight: 600,
                  cursor: unsaved && !savingSymbols ? 'pointer' : 'not-allowed',
                }}
              >{savingSymbols ? '저장 중...' : '💾 저장'}</button>
            </div>

            {/* 선택된 룰 미사용 경고 */}
            {selRuleDetail && (() => {
              const currentRule = rules.find(r => String(r.id) === selSymbolRuleId);
              if (currentRule && currentRule.accountCount === 0) {
                return (
                  <div style={{
                    padding: '10px 16px', marginBottom: '12px',
                    background: 'rgba(255,224,102,0.08)',
                    border: '1px solid rgba(255,224,102,0.3)',
                    borderRadius: '8px',
                    color: '#ffe066', fontSize: '12px', fontWeight: 600,
                  }}>
                    ⚠ 이 룰(ID {currentRule.id})을 사용하는 활성 계좌가 없습니다. 여기서 설정해도 실제 거래에는 적용되지 않습니다.
                  </div>
                );
              }
              return null;
            })()}

            {/* 필터/검색 + 일괄 설정 */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px',
              padding: '10px 16px',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '8px', flexWrap: 'wrap',
            }}>
              <input
                type="text"
                placeholder="🔍 심볼 검색 (AUDCAD, Gold...)"
                value={symbolTabSearch}
                onChange={e => setSymbolTabSearch(e.target.value)}
                style={{
                  background: '#1a1a2e',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px', color: '#fff',
                  padding: '6px 12px', fontSize: '13px',
                  minWidth: '220px',
                }}
              />
              <select
                value={symbolTabGroupFilter}
                onChange={e => setSymbolTabGroupFilter(e.target.value)}
                style={{
                  background: '#1a1a2e',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px', color: '#fff',
                  padding: '6px 12px', fontSize: '13px',
                  minWidth: '140px', cursor: 'pointer',
                  colorScheme: 'dark',
                }}
              >
                <option value="ALL" style={{ background: '#1a1a2e', color: '#fff' }}>전체 그룹</option>
                {groupOptions.map(g => (
                  <option key={g} value={g} style={{ background: '#1a1a2e', color: '#fff' }}>{g}</option>
                ))}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#fff', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={symbolTabShowChangedOnly}
                  onChange={e => setSymbolTabShowChangedOnly(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span>커스텀만 보기</span>
              </label>

              <div style={{ flex: 1 }} />

              {/* 일괄 설정 */}
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>💡 현재 필터({filtered.length}개) 일괄 설정:</span>
              <input
                type="number"
                placeholder="포인트"
                value={bulkEditValue}
                onChange={e => setBulkEditValue(e.target.value)}
                style={{
                  background: '#1a1a2e',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px', color: '#fff',
                  padding: '6px 10px', fontSize: '13px',
                  width: '90px',
                }}
              />
              <button
                onClick={() => {
                  if (!bulkEditValue || isNaN(Number(bulkEditValue))) { alert('포인트 값을 입력하세요'); return; }
                  if (!window.confirm(`필터된 ${filtered.length}개 심볼에 ${bulkEditValue}pt 설정?`)) return;
                  const next = { ...symbolPointsEdit };
                  filtered.forEach((sym: any) => { next[sym.symbol] = Number(bulkEditValue); });
                  setSymbolPointsEdit(next);
                }}
                style={{
                  background: 'rgba(102,126,234,0.2)',
                  border: '1px solid rgba(102,126,234,0.5)',
                  color: '#667eea',
                  padding: '6px 14px', borderRadius: '6px',
                  fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                }}
              >일괄 적용</button>
              <button
                onClick={() => {
                  if (!window.confirm(`필터된 ${filtered.length}개 심볼을 기본값(${defaultPoint}pt)으로?`)) return;
                  const next = { ...symbolPointsEdit };
                  filtered.forEach((sym: any) => { delete next[sym.symbol]; });
                  setSymbolPointsEdit(next);
                }}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  padding: '6px 14px', borderRadius: '6px',
                  fontSize: '12px', cursor: 'pointer',
                }}
              >기본값으로</button>
            </div>

            {/* 테이블 */}
            <div style={s.tableWrap}>
              {filtered.length === 0 ? (
                <div style={s.empty}>필터 조건에 맞는 심볼이 없습니다</div>
              ) : (
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, width: '200px' }}>심볼</th>
                      <th style={s.th}>설명</th>
                      <th style={{ ...s.th, width: '120px' }}>포인트</th>
                      {selRuleDetail?.forceEntryEnabled && (
                        <>
                          <th style={{ ...s.th, width: '120px', color: '#2ed573' }}>⏱ 시간(초)</th>
                          <th style={{ ...s.th, width: '120px', color: '#2ed573' }}>⚡ 포인트강제pt</th>
                        </>
                      )}
                      <th style={{ ...s.th, width: '100px' }}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupKeys.map(groupName => {
                      const syms = grouped[groupName];
                      const isCollapsed = collapsedSymbolGroups.has(groupName);
                      const groupChangedCount = syms.filter((sym: any) => {
                        const cur = symbolPointsEdit[sym.symbol];
                        const orig = symbolPointsOriginal[sym.symbol];
                        const curNum = cur === '' || cur == null ? null : Number(cur);
                        const origNum = orig === '' || orig == null ? null : Number(orig);
                        return curNum !== origNum;
                      }).length;
                      return (
                        <React.Fragment key={groupName}>
                          <tr
                            style={{ background: 'rgba(102,126,234,0.12)', cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => {
                              const next = new Set(collapsedSymbolGroups);
                              if (next.has(groupName)) next.delete(groupName);
                              else next.add(groupName);
                              setCollapsedSymbolGroups(next);
                            }}
                          >
                            <td style={{ ...s.td, color: '#667eea' }} colSpan={2}>
                              {isCollapsed ? '▶' : '▼'} 📂 {groupName} ({syms.length}개)
                              {groupChangedCount > 0 && (
                                <span style={{ color: '#ffe066', fontSize: '11px', marginLeft: '10px' }}>
                                  🟡 {groupChangedCount}개 변경됨
                                </span>
                              )}
                            </td>
                            <td style={s.td}>
                              {/* 그룹 일괄 적용 - 포인트 */}
                              <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                                <input
                                  type="number"
                                  placeholder="pt"
                                  style={{
                                    background: '#1a1a2e',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    borderRadius: '4px', color: '#fff',
                                    padding: '4px 6px', fontSize: '11px',
                                    width: '60px',
                                  }}
                                  onKeyDown={(e: any) => {
                                    if (e.key === 'Enter') {
                                      const v = e.target.value;
                                      if (!v) return;
                                      if (!window.confirm(`${groupName} 그룹 ${syms.length}개 심볼에 ${v}pt 설정?`)) return;
                                      const next = { ...symbolPointsEdit };
                                      syms.forEach((sy: any) => { next[sy.symbol] = Number(v); });
                                      setSymbolPointsEdit(next);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                              </div>
                            </td>
                            {selRuleDetail?.forceEntryEnabled && (
                              <>
                                <td style={s.td}>
                                  {/* 그룹 일괄 - 시간(초) */}
                                  <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                                    <input
                                      type="number"
                                      placeholder="초"
                                      style={{
                                        background: '#1a1a2e',
                                        border: '1px solid rgba(46,213,115,0.3)',
                                        borderRadius: '4px', color: '#fff',
                                        padding: '4px 6px', fontSize: '11px',
                                        width: '60px',
                                      }}
                                      onKeyDown={(e: any) => {
                                        if (e.key === 'Enter') {
                                          const v = e.target.value;
                                          if (!v) return;
                                          if (!window.confirm(`${groupName} 그룹 ${syms.length}개 심볼에 시간 ${v}초 설정?`)) return;
                                          const next = { ...symbolTimeoutsEdit };
                                          syms.forEach((sy: any) => { next[sy.symbol] = Number(v); });
                                          setSymbolTimeoutsEdit(next);
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                  </div>
                                </td>
                                <td style={s.td}>
                                  {/* 그룹 일괄 - 포인트 강제진입 */}
                                  <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                                    <input
                                      type="number"
                                      placeholder="pt"
                                      style={{
                                        background: '#1a1a2e',
                                        border: '1px solid rgba(46,213,115,0.3)',
                                        borderRadius: '4px', color: '#fff',
                                        padding: '4px 6px', fontSize: '11px',
                                        width: '60px',
                                      }}
                                      onKeyDown={(e: any) => {
                                        if (e.key === 'Enter') {
                                          const v = e.target.value;
                                          if (!v) return;
                                          if (!window.confirm(`${groupName} 그룹 ${syms.length}개 심볼에 포인트강제 ${v}pt 설정?`)) return;
                                          const next = { ...symbolAdverseEdit };
                                          syms.forEach((sy: any) => { next[sy.symbol] = Number(v); });
                                          setSymbolAdverseEdit(next);
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                  </div>
                                </td>
                              </>
                            )}
                            <td style={s.td}></td>
                          </tr>
                          {!isCollapsed && syms.map((sym: any, idx: number) => {
                            const cur = symbolPointsEdit[sym.symbol];
                            const orig = symbolPointsOriginal[sym.symbol];
                            const curNum = cur === '' || cur == null ? null : Number(cur);
                            const origNum = orig === '' || orig == null ? null : Number(orig);
                            const isChanged = curNum !== origNum;
                            const isCustom = curNum != null; // 기본값 아님
                            return (
                              <tr key={sym.symbol} style={{
                                ...(idx % 2 === 0 ? s.trEven : s.trOdd),
                                ...(isChanged ? { background: 'rgba(255,224,102,0.08)' } : {}),
                              }}>
                                <td style={{ ...s.td, paddingLeft: '32px' }}>
                                  <span style={{ color: '#667eea', fontFamily: 'monospace', fontWeight: 600 }}>{sym.symbol}</span>
                                </td>
                                <td style={s.td}>
                                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>{sym.description || '-'}</span>
                                </td>
                                <td style={s.td}>
                                  <input
                                    type="number"
                                    value={cur ?? ''}
                                    placeholder={`기본 ${defaultPoint}`}
                                    onChange={e => {
                                      const v = e.target.value;
                                      const next = { ...symbolPointsEdit };
                                      if (v === '') delete next[sym.symbol];
                                      else next[sym.symbol] = Number(v);
                                      setSymbolPointsEdit(next);
                                    }}
                                    style={{
                                      background: '#1a1a2e',
                                      border: '1px solid ' + (isChanged ? 'rgba(255,224,102,0.5)' : 'rgba(255,255,255,0.15)'),
                                      borderRadius: '4px', color: '#fff',
                                      padding: '5px 10px', fontSize: '13px',
                                      width: '100px',
                                      fontFamily: 'monospace',
                                    }}
                                  />
                                </td>
                                {selRuleDetail?.forceEntryEnabled && (() => {
                                  const curT = symbolTimeoutsEdit[sym.symbol];
                                  const origT = symbolTimeoutsOriginal[sym.symbol];
                                  const curTNum = curT === '' || curT == null ? null : Number(curT);
                                  const origTNum = origT === '' || origT == null ? null : Number(origT);
                                  const isTChanged = curTNum !== origTNum;
                                  const curA = symbolAdverseEdit[sym.symbol];
                                  const origA = symbolAdverseOriginal[sym.symbol];
                                  const curANum = curA === '' || curA == null ? null : Number(curA);
                                  const origANum = origA === '' || origA == null ? null : Number(origA);
                                  const isAChanged = curANum !== origANum;
                                  const defT = selRuleDetail.defaultTimeoutSeconds || 300;
                                  const defA = selRuleDetail.defaultAdversePoint || 50;
                                  return (
                                    <>
                                      <td style={s.td}>
                                        <input
                                          type="number"
                                          value={curT ?? ''}
                                          placeholder={`기본 ${defT}`}
                                          onChange={e => {
                                            const v = e.target.value;
                                            const next = { ...symbolTimeoutsEdit };
                                            if (v === '') delete next[sym.symbol];
                                            else next[sym.symbol] = Number(v);
                                            setSymbolTimeoutsEdit(next);
                                          }}
                                          style={{
                                            background: '#1a1a2e',
                                            border: '1px solid ' + (isTChanged ? 'rgba(255,224,102,0.5)' : 'rgba(46,213,115,0.25)'),
                                            borderRadius: '4px', color: '#fff',
                                            padding: '5px 10px', fontSize: '13px',
                                            width: '100px',
                                            fontFamily: 'monospace',
                                          }}
                                        />
                                      </td>
                                      <td style={s.td}>
                                        <input
                                          type="number"
                                          value={curA ?? ''}
                                          placeholder={`기본 ${defA}`}
                                          onChange={e => {
                                            const v = e.target.value;
                                            const next = { ...symbolAdverseEdit };
                                            if (v === '') delete next[sym.symbol];
                                            else next[sym.symbol] = Number(v);
                                            setSymbolAdverseEdit(next);
                                          }}
                                          style={{
                                            background: '#1a1a2e',
                                            border: '1px solid ' + (isAChanged ? 'rgba(255,224,102,0.5)' : 'rgba(46,213,115,0.25)'),
                                            borderRadius: '4px', color: '#fff',
                                            padding: '5px 10px', fontSize: '13px',
                                            width: '100px',
                                            fontFamily: 'monospace',
                                          }}
                                        />
                                      </td>
                                    </>
                                  );
                                })()}
                                <td style={s.td}>
                                  {isChanged ? (
                                    <span style={{ color: '#ffe066', fontSize: '11px', fontWeight: 600 }}>🟡 변경됨</span>
                                  ) : isCustom ? (
                                    <span style={{ color: '#2ed573', fontSize: '11px' }}>✓ 커스텀</span>
                                  ) : (
                                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>기본값</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── 계좌 관리 탭 ── */}
      {tab === 'accounts' && (
        <div>
          {/* 상단 필터 + 액션 바 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {/* 룰 필터 select */}
            <select
              value={accountFilterRule}
              onChange={e => setAccountFilterRule(e.target.value)}
              style={{
                background: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '6px',
                color: '#fff',
                padding: '8px 12px',
                fontSize: '13px',
                minWidth: '180px',
                cursor: 'pointer',
                colorScheme: 'dark',
              }}
            >
              <option value="ALL" style={{ background: '#1a1a2e', color: '#fff' }}>📍 전체 룰</option>
              {rules.map(r => (
                <option key={r.id} value={String(r.id)} style={{ background: '#1a1a2e', color: '#fff' }}>
                  📍 {r.name} · ID {r.id} · 계좌 {r.accountCount}개
                </option>
              ))}
            </select>

            {/* 검색 input */}
            <input
              type="text"
              placeholder="계좌번호 또는 이름 검색..."
              value={accountSearch}
              onChange={e => setAccountSearch(e.target.value)}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '6px',
                color: '#fff',
                padding: '8px 12px',
                fontSize: '13px',
                minWidth: '220px',
                flex: '0 1 300px',
              }}
            />

            {/* 선택된 계좌 수 표시 */}
            {accountCheckedIds.size > 0 && (
              <span style={{ color: '#f7971e', fontSize: '13px', fontWeight: 600 }}>
                {accountCheckedIds.size}개 선택됨
              </span>
            )}

            {/* 여백 밀어내기 */}
            <div style={{ flex: 1 }} />

            {/* 일괄 비활성화 버튼 (선택이 있을 때만) */}
            {accountCheckedIds.size > 0 && (
              <button
                style={{
                  background: 'rgba(255,107,107,0.15)',
                  border: '1px solid rgba(255,107,107,0.4)',
                  color: '#ff6b6b',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onClick={async () => {
                  if (!window.confirm(`${accountCheckedIds.size}개 계좌를 비활성화하시겠습니까?`)) return;
                  const ids = Array.from(accountCheckedIds);
                  for (const id of ids) {
                    try { await api.delete(`/v1/point-rule/accounts/${id}`); } catch (e) {}
                  }
                  setAccountCheckedIds(new Set());
                  fetchAccounts();
                  fetchStatistics();
                }}
              >
                선택 비활성화
              </button>
            )}

            <button style={s.addBtn} onClick={() => setShowAccountModal(true)}>+ 계좌 등록</button>
          </div>

          {(() => {
            // 데이터 병합: accounts + statistics
            const statsMap: Record<string, any> = {};
            statistics.forEach(s => { statsMap[s.accountId] = s; });
            const merged = accounts.map(a => ({ ...a, ...(statsMap[a.accountId] || {}) }));

            // 필터링
            const filtered = merged.filter(a => {
              if (accountFilterRule !== 'ALL' && String(a.ruleId) !== accountFilterRule) return false;
              if (accountSearch.trim()) {
                const q = accountSearch.trim().toLowerCase();
                const name = (equityMap[a.accountId]?.name || a.name || '').toLowerCase();
                const id = String(a.accountId).toLowerCase();
                if (!name.includes(q) && !id.includes(q)) return false;
              }
              return true;
            });

            const allChecked = filtered.length > 0 && filtered.every(a => accountCheckedIds.has(a.accountId));
            const someChecked = filtered.some(a => accountCheckedIds.has(a.accountId));

            return (
              <div style={s.tableWrap}>
                {loading ? <div style={s.loading}>로딩 중...</div> :
                  filtered.length === 0 ? <div style={s.empty}>
                    {accounts.length === 0 ? '등록된 계좌가 없습니다' : '검색 조건에 맞는 계좌가 없습니다'}
                  </div> : (
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th, width: '36px' }}>
                          <input
                            type="checkbox"
                            checked={allChecked}
                            ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                            onChange={e => {
                              const next = new Set(accountCheckedIds);
                              if (e.target.checked) filtered.forEach(a => next.add(a.accountId));
                              else filtered.forEach(a => next.delete(a.accountId));
                              setAccountCheckedIds(next);
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th style={s.th}>계좌</th>
                        <th style={s.th}>이름</th>
                        <th style={s.th}>Balance</th>
                        <th style={s.th}>Equity</th>
                        <th style={s.th}>Floating</th>
                        <th style={s.th}>적용 룰</th>
                        <th style={s.th}>전체</th>
                        <th style={s.th}>예약중</th>
                        <th style={s.th}>체결됨</th>
                        <th style={s.th}>취소됨</th>
                        <th style={s.th}>청산완료</th>
                        <th style={s.th}>⏰시간강제</th>
                        <th style={s.th}>⚡포인트강제</th>
                        <th style={s.th}>체결률</th>
                        <th style={s.th}>Agent 비용</th>
                        <th style={s.th}>브로커 수익</th>
                        <th style={s.th}>상세</th>
                        <th style={s.th}>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((a, idx) => {
                        const eq = equityMap[a.accountId] || {};
                        const fl = floatingMap[a.accountId];
                        const floatingVal = fl?.floating_total;
                        const hasPos = fl?.has_position;
                        const isExpanded = selectedAccount === a.accountId;
                        const isChecked = accountCheckedIds.has(a.accountId);
                        return (
                          <React.Fragment key={a.accountId}>
                            <tr style={{ ...(idx % 2 === 0 ? s.trEven : s.trOdd), ...(isExpanded ? { background: 'rgba(102,126,234,0.1)' } : {}) }}>
                              <td style={s.td}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={e => {
                                    const next = new Set(accountCheckedIds);
                                    if (e.target.checked) next.add(a.accountId);
                                    else next.delete(a.accountId);
                                    setAccountCheckedIds(next);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                />
                              </td>
                              <td style={s.td}><span style={s.accId}>{a.accountId}</span></td>
                              <td style={s.td}>{eq.name || a.name || '-'}</td>
                              <td style={s.td}>
                                <FlashValue value={eq.balance}
                                  format={v => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                              </td>
                              <td style={s.td}>
                                <FlashValue value={eq.equity}
                                  format={v => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                              </td>
                              <td style={s.td}>
                                {floatingVal != null ? (
                                  <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, color: flashFloating[a.accountId] ? '#ffe066' : (hasPos === false ? 'rgba(255,255,255,0.4)' : (floatingVal >= 0 ? '#2ed573' : '#ff6b6b')) }}>
                                    {fmt(floatingVal)}
                                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginLeft: '4px' }}>
                                      ({hasPos === false ? '무' : '건'})
                                    </span>
                                  </span>
                                ) : <span style={{ color: 'rgba(255,255,255,0.2)', fontFamily: 'monospace' }}>-</span>}
                              </td>
                              <td style={s.td}>
                                {a.ruleName ? (
                                  <span>
                                    <span style={{ color: '#f7971e', fontSize: '12px', fontWeight: 600 }}>📍 {a.ruleName}</span>
                                    {a.ruleId != null && (
                                      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginLeft: '4px', fontFamily: 'monospace' }}>#{a.ruleId}</span>
                                    )}
                                  </span>
                                ) : <span style={{ color: 'rgba(255,255,255,0.3)' }}>미설정</span>}
                              </td>
                              <td style={s.td}><span style={s.mono}>{a.totalPositions ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{a.pendingCount ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{a.filledCount ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{a.cancelledCount ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{a.closedCount ?? 0}</span></td>

                              <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{a.forcedByTimeCount ?? 0}</span></td>

                              <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{a.forcedByAdverseCount ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{a.filledRate || '0%'}</span></td>
                              <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(a.agentCost || 0)))}</span></td>
                              <td style={s.td}><span style={{ color: pnlColor(a.brokerProfit || 0), fontFamily: 'monospace', fontWeight: 700 }}>{fmt(a.brokerProfit || 0)}</span></td>
                              <td style={s.td}>
                                <button
                                  style={{ ...s.editBtn, ...(isExpanded ? { background: 'rgba(102,126,234,0.3)' } : {}) }}
                                  onClick={() => {
                                    if (isExpanded) {
                                      setSelectedAccount(null);
                                      setAccountDetail(null);
                                    } else {
                                      fetchAccountDetail(a.accountId);
                                    }
                                  }}
                                >{isExpanded ? '접기 ▲' : '상세 ▼'}</button>
                              </td>
                              <td style={s.td}>
                                <button style={s.delBtn} onClick={() => removeAccount(a.accountId)}>비활성화</button>
                              </td>
                            </tr>
                            {isExpanded && accountDetail && (
                              <tr>
                                <td colSpan={19} style={{ padding: 0, background: 'rgba(0,0,0,0.3)' }}>
                                  <div style={{ padding: '16px 24px', borderLeft: '3px solid #667eea' }}>
                                    <h3 style={{ ...s.detailTitle, marginTop: 0, marginBottom: '12px', fontSize: '14px' }}>계좌 {a.accountId} 포지션 내역</h3>
                                    <table style={s.table}>
                                      <thead>
                                        <tr>
                                          <th style={s.th}>티켓</th>
                                          <th style={s.th}>심볼</th>
                                          <th style={s.th}>방향</th>
                                          <th style={s.th}>랏수</th>
                                          <th style={s.th}>고객 진입가</th>
                                          <th style={s.th}>마스터 진입가</th>
                                          <th style={s.th}>갭(pt)</th>
                                          <th style={s.th}>상태</th>
                                          <th style={s.th}>고객 손익</th>
                                          <th style={s.th}>마스터 손익</th>
                                          <th style={s.th}>Agent 비용</th>
                                          <th style={s.th}>브로커 수익</th>
                                          <th style={s.th}>진입시각</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(accountDetail.positions || []).map((p: any, pidx: number) => (
                                          <tr key={p.id} style={pidx % 2 === 0 ? s.trEven : s.trOdd}>
                                            <td style={s.td}><span style={s.mono}>{p.customerTicket}</span></td>
                                            <td style={s.td}><span style={{ ...s.mono, color: '#667eea' }}>{p.symbol}</span></td>
                                            <td style={s.td}><span style={{ color: p.direction === 'BUY' ? '#2ed573' : '#ff6b6b', fontWeight: 700 }}>{p.direction}</span></td>
                                            <td style={s.td}><span style={s.mono}>{p.lots}</span></td>
                                            <td style={s.td}><span style={s.mono}>{p.customerOpenPrice}</span></td>
                                            <td style={s.td}><span style={{ ...s.mono, color: '#f7971e' }}>{p.masterOpenPrice != null ? p.masterOpenPrice : '-'}</span></td>
                                            <td style={s.td}><span style={{ ...s.mono, color: p.entryGapPoint != null ? (Number(p.entryGapPoint) >= 0 ? '#2ed573' : '#ff6b6b') : '#888' }}>{p.entryGapPoint != null ? (Number(p.entryGapPoint) > 0 ? '+' : '') + Number(p.entryGapPoint).toFixed(2) : '-'}</span></td>
                                            <td style={s.td}>
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ color: statusColor(p.status), fontSize: '12px', fontWeight: 600 }}>{statusLabel(p.status)}</span>
                                                {entryTypeBadge(p.entryType) && (
                                                  <span style={{ color: entryTypeBadge(p.entryType)!.color, fontSize: '10px', fontWeight: 600 }}>
                                                    {entryTypeBadge(p.entryType)!.icon} {entryTypeBadge(p.entryType)!.label}
                                                  </span>
                                                )}
                                              </div>
                                            </td>
                                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(p.customerProfit || 0) }}>{p.customerProfit != null ? fmt(p.customerProfit) : '-'}</span></td>
                                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(p.masterProfit || 0) }}>{p.masterProfit != null ? fmt(p.masterProfit) : '-'}</span></td>
                                            <td style={s.td}><span style={{ ...s.mono, color: '#ff6b6b' }}>{p.agentCost != null ? fmt(-Math.abs(p.agentCost)) : '-'}</span></td>
                                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(p.brokerProfit || 0), fontWeight: 700 }}>{p.brokerProfit != null ? fmt(p.brokerProfit) : '-'}</span></td>
                                            <td style={s.td}><span style={{ ...s.mono, fontSize: '11px' }}>{p.openedAt ? new Date(p.openedAt).toLocaleString('ko-KR') : '-'}</span></td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── 통계 탭 ── */}
      {tab === 'statistics' && (
        <div>
          {/* 공통 필터 바 */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            marginBottom: '16px', padding: '12px 16px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px', flexWrap: 'wrap'
          }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600 }}>필터</span>

            {/* 룰 select */}
            <select
              value={statsRuleId}
              onChange={e => setStatsRuleId(e.target.value)}
              style={{
                background: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '6px', color: '#fff',
                padding: '6px 12px', fontSize: '13px',
                minWidth: '160px', cursor: 'pointer',
                colorScheme: 'dark',
              }}
            >
              <option value="ALL" style={{ background: '#1a1a2e', color: '#fff' }}>📍 전체 룰</option>
              {rules.map(r => (
                <option key={r.id} value={String(r.id)} style={{ background: '#1a1a2e', color: '#fff' }}>
                  📍 {r.name} · ID {r.id}
                </option>
              ))}
            </select>

            {/* 상태 select */}
            <select
              value={statsStatus}
              onChange={e => setStatsStatus(e.target.value)}
              style={{
                background: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '6px', color: '#fff',
                padding: '6px 12px', fontSize: '13px',
                minWidth: '140px', cursor: 'pointer',
                colorScheme: 'dark',
              }}
            >
              <option value="ALL" style={{ background: '#1a1a2e', color: '#fff' }}>🔹 전체 상태</option>
              <option value="CLOSED" style={{ background: '#1a1a2e', color: '#fff' }}>✅ 청산완료</option>
              <option value="CANCELLED" style={{ background: '#1a1a2e', color: '#fff' }}>⛔ 취소됨</option>
              <option value="FILLED" style={{ background: '#1a1a2e', color: '#fff' }}>🟢 체결됨</option>
              <option value="PENDING" style={{ background: '#1a1a2e', color: '#fff' }}>⏳ 예약중</option>
              <option value="FORCED_BY_TIME" style={{ background: '#1a1a2e', color: '#fff' }}>⏰ 시간 강제 진입</option>
              <option value="FORCED_BY_ADVERSE_POINT" style={{ background: '#1a1a2e', color: '#fff' }}>⚡ 포인트 강제 진입</option>
            </select>

            {/* 기간 */}
            <input
              type="date"
              value={statsFrom}
              onChange={e => setStatsFrom(e.target.value)}
              style={{
                background: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '6px', color: '#fff',
                padding: '6px 10px', fontSize: '13px',
                colorScheme: 'dark',
              }}
            />
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>~</span>
            <input
              type="date"
              value={statsTo}
              onChange={e => setStatsTo(e.target.value)}
              style={{
                background: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '6px', color: '#fff',
                padding: '6px 10px', fontSize: '13px',
                colorScheme: 'dark',
              }}
            />

            {/* 빠른 기간 선택 */}
            {[
              { label: '전체', fn: () => { setStatsFrom(''); setStatsTo(''); } },
              { label: '오늘', fn: () => {
                const d = new Date().toISOString().slice(0,10);
                setStatsFrom(d); setStatsTo(d);
              }},
              { label: '이번주', fn: () => {
                const now = new Date();
                const day = now.getDay() || 7;
                const monday = new Date(now); monday.setDate(now.getDate() - day + 1);
                setStatsFrom(monday.toISOString().slice(0,10));
                setStatsTo(now.toISOString().slice(0,10));
              }},
              { label: '이번달', fn: () => {
                const now = new Date();
                const first = new Date(now.getFullYear(), now.getMonth(), 1);
                setStatsFrom(first.toISOString().slice(0,10));
                setStatsTo(now.toISOString().slice(0,10));
              }},
              { label: '지난달', fn: () => {
                const now = new Date();
                const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const last = new Date(now.getFullYear(), now.getMonth(), 0);
                setStatsFrom(first.toISOString().slice(0,10));
                setStatsTo(last.toISOString().slice(0,10));
              }},
            ].map(({ label, fn }) => (
              <button
                key={label}
                onClick={fn}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#fff',
                  padding: '5px 12px', borderRadius: '4px',
                  fontSize: '12px', cursor: 'pointer',
                }}
              >{label}</button>
            ))}

            <div style={{ flex: 1 }} />

            {/* 현재 선택된 기간 표시 */}
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
              {statsFrom || statsTo ? `${statsFrom || '처음'} ~ ${statsTo || '지금'}` : '전체 기간'}
            </span>
          </div>

          {/* 서브탭 버튼 */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            {[
              { key: 'accounts' as const, label: '📊 계좌별' },
              { key: 'symbols' as const, label: '💱 통화별' },
              { key: 'daily' as const, label: '📅 일별' },
              { key: 'monthly' as const, label: '🗓️ 월별' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatsSubTab(key)}
                style={{
                  background: statsSubTab === key ? 'rgba(102,126,234,0.2)' : 'transparent',
                  border: 'none',
                  borderBottom: statsSubTab === key ? '2px solid #667eea' : '2px solid transparent',
                  color: statsSubTab === key ? '#667eea' : 'rgba(255,255,255,0.6)',
                  padding: '10px 20px',
                  fontSize: '13px', fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '-1px',
                }}
              >{label}</button>
            ))}
          </div>

          {/* ── 서브탭: 계좌별 ── */}
          {statsSubTab === 'accounts' && (
          <div style={s.tableWrap}>
            {loading ? <div style={s.loading}>로딩 중...</div> :
              statistics.length === 0 ? <div style={s.empty}>데이터가 없습니다</div> : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>계좌</th>
                    <th style={s.th}>이름</th>
                    <th style={s.th}>Balance</th>
                    <th style={s.th}>Equity</th>
                    <th style={s.th}>Floating</th>
                    <th style={s.th}>적용 룰</th>
                    <th style={s.th}>전체</th>
                    <th style={s.th}>예약중</th>
                    <th style={s.th}>체결됨</th>
                    <th style={s.th}>취소됨</th>
                    <th style={s.th}>청산완료</th>
                    <th style={s.th}>⏰시간강제</th>
                    <th style={s.th}>⚡포인트강제</th>
                    <th style={s.th}>체결률</th>
                    <th style={s.th}>평균 랏</th>
                    <th style={s.th}>평균 보유</th>
                    <th style={s.th}>승률</th>
                    <th style={s.th}>Agent 비용</th>
                    <th style={s.th}>브로커 수익</th>
                    <th style={s.th}>상세</th>
                  </tr>
                </thead>
                <tbody>
                  {statistics.map((a, idx) => {
                    const eq = equityMap[a.accountId] || {};
                    const fl = floatingMap[a.accountId];
                    const floatingVal = fl?.floating_total;
                    const hasPos = fl?.has_position;
                    const isExpanded = selectedAccount === a.accountId;
                    return (
                      <React.Fragment key={a.accountId}>
                        <tr style={{ ...(idx % 2 === 0 ? s.trEven : s.trOdd), ...(isExpanded ? { background: 'rgba(102,126,234,0.1)' } : {}) }}>
                          <td style={s.td}><span style={s.accId}>{a.accountId}</span></td>
                          <td style={s.td}>{eq.name || a.name || '-'}</td>
                          <td style={s.td}>
                            <FlashValue value={eq.balance}
                              format={v => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                          </td>
                          <td style={s.td}>
                            <FlashValue value={eq.equity}
                              format={v => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                          </td>
                          <td style={s.td}>
                            {floatingVal != null ? (
                              <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, color: flashFloating[a.accountId] ? '#ffe066' : (hasPos === false ? 'rgba(255,255,255,0.4)' : (floatingVal >= 0 ? '#2ed573' : '#ff6b6b')) }}>
                                {fmt(floatingVal)}
                                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginLeft: '4px' }}>
                                  ({hasPos === false ? '무' : '건'})
                                </span>
                              </span>
                            ) : <span style={{ color: 'rgba(255,255,255,0.2)', fontFamily: 'monospace' }}>-</span>}
                          </td>
                          <td style={s.td}>
                            {a.ruleName ? (
                              <span>
                                <span style={{ color: '#f7971e', fontSize: '12px' }}>{a.ruleName}</span>
                                {a.ruleId != null && (
                                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginLeft: '4px', fontFamily: 'monospace' }}>#{a.ruleId}</span>
                                )}
                              </span>
                            ) : '-'}
                          </td>
                          <td style={s.td}><span style={s.mono}>{a.totalPositions}</span></td>
                          <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{a.pendingCount}</span></td>
                          <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{a.filledCount}</span></td>
                          <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{a.cancelledCount}</span></td>
                          <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{a.closedCount}</span></td>

                          <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{a.forcedByTimeCount}</span></td>

                          <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{a.forcedByAdverseCount}</span></td>
                          <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{a.filledRate}</span></td>
                          <td style={s.td}><span style={{ color: '#fff', fontFamily: 'monospace', fontSize: '12px' }}>{a.avgLots != null ? Number(a.avgLots).toFixed(2) : '-'}</span></td>
                          <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.75)', fontFamily: 'monospace', fontSize: '12px' }}>{formatHold(a.avgHoldSec)}</span></td>
                          <td style={s.td}><span style={{ color: winRateColor(a.winRate), fontFamily: 'monospace', fontWeight: 600 }}>{a.winRate || '-'}</span></td>
                          <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(a.agentCost || 0)))}</span></td>
                          <td style={s.td}><span style={{ color: pnlColor(a.brokerProfit || 0), fontFamily: 'monospace', fontWeight: 700 }}>{fmt(a.brokerProfit || 0)}</span></td>
                          <td style={s.td}>
                            <button
                              style={{ ...s.editBtn, ...(isExpanded ? { background: 'rgba(102,126,234,0.3)' } : {}) }}
                              onClick={() => {
                                if (isExpanded) {
                                  setSelectedAccount(null);
                                  setAccountDetail(null);
                                } else {
                                  fetchAccountDetail(a.accountId, {
                                    status: statsStatus,
                                    from: statsFrom,
                                    to: statsTo,
                                  });
                                }
                              }}
                            >{isExpanded ? '접기 ▲' : '상세 ▼'}</button>
                          </td>
                        </tr>
                        {isExpanded && accountDetail && (
                          <tr>
                            <td colSpan={20} style={{ padding: 0, background: 'rgba(0,0,0,0.3)' }}>
                              <div style={{ padding: '16px 24px', borderLeft: '3px solid #667eea' }}>
                                <h3 style={{ ...s.detailTitle, marginTop: 0, marginBottom: '12px', fontSize: '14px' }}>계좌 {a.accountId} 포지션 내역</h3>
                                <table style={s.table}>
                                  <thead>
                                    <tr>
                                      <th style={s.th}>티켓</th>
                                      <th style={s.th}>심볼</th>
                                      <th style={s.th}>방향</th>
                                      <th style={s.th}>랏수</th>
                                      <th style={s.th}>고객 진입가</th>
                                      <th style={s.th}>마스터 진입가</th>
                                          <th style={s.th}>갭(pt)</th>
                                      <th style={s.th}>상태</th>
                                      <th style={s.th}>고객 손익</th>
                                      <th style={s.th}>마스터 손익</th>
                                      <th style={s.th}>Agent 비용</th>
                                      <th style={s.th}>브로커 수익</th>
                                      <th style={s.th}>진입시각</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(accountDetail.positions || []).map((p: any, pidx: number) => (
                                      <tr key={p.id} style={pidx % 2 === 0 ? s.trEven : s.trOdd}>
                                        <td style={s.td}><span style={s.mono}>{p.customerTicket}</span></td>
                                        <td style={s.td}><span style={{ ...s.mono, color: '#667eea' }}>{p.symbol}</span></td>
                                        <td style={s.td}><span style={{ color: p.direction === 'BUY' ? '#2ed573' : '#ff6b6b', fontWeight: 700 }}>{p.direction}</span></td>
                                        <td style={s.td}><span style={s.mono}>{p.lots}</span></td>
                                        <td style={s.td}><span style={s.mono}>{p.customerOpenPrice}</span></td>
                                        <td style={s.td}><span style={{ ...s.mono, color: '#f7971e' }}>{p.masterOpenPrice != null ? p.masterOpenPrice : '-'}</span></td>
                                        <td style={s.td}><span style={{ ...s.mono, color: p.entryGapPoint != null ? (Number(p.entryGapPoint) >= 0 ? '#2ed573' : '#ff6b6b') : '#888' }}>{p.entryGapPoint != null ? (Number(p.entryGapPoint) > 0 ? '+' : '') + Number(p.entryGapPoint).toFixed(2) : '-'}</span></td>
                                        <td style={s.td}>
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ color: statusColor(p.status), fontSize: '12px', fontWeight: 600 }}>{statusLabel(p.status)}</span>
                                                {entryTypeBadge(p.entryType) && (
                                                  <span style={{ color: entryTypeBadge(p.entryType)!.color, fontSize: '10px', fontWeight: 600 }}>
                                                    {entryTypeBadge(p.entryType)!.icon} {entryTypeBadge(p.entryType)!.label}
                                                  </span>
                                                )}
                                              </div>
                                            </td>
                                        <td style={s.td}><span style={{ ...s.mono, color: pnlColor(p.customerProfit || 0) }}>{p.customerProfit != null ? fmt(p.customerProfit) : '-'}</span></td>
                                        <td style={s.td}><span style={{ ...s.mono, color: pnlColor(p.masterProfit || 0) }}>{p.masterProfit != null ? fmt(p.masterProfit) : '-'}</span></td>
                                        <td style={s.td}><span style={{ ...s.mono, color: '#ff6b6b' }}>{p.agentCost != null ? fmt(-Math.abs(p.agentCost)) : '-'}</span></td>
                                        <td style={s.td}><span style={{ ...s.mono, color: pnlColor(p.brokerProfit || 0), fontWeight: 700 }}>{p.brokerProfit != null ? fmt(p.brokerProfit) : '-'}</span></td>
                                        <td style={s.td}><span style={{ ...s.mono, fontSize: '11px' }}>{p.openedAt ? new Date(p.openedAt).toLocaleString('ko-KR') : '-'}</span></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* ── 서브탭: 통화별 ── */}
          {statsSubTab === 'symbols' && (
            <div>
              {/* 차트 - 심볼별 브로커 수익 top 15 */}
              {(() => {
                const allSymbols: any[] = [];
                (symbolStats.groups || []).forEach((g: any) => {
                  (g.symbols || []).forEach((s: any) => {
                    allSymbols.push({ ...s, groupName: g.groupName });
                  });
                });
                const top = [...allSymbols]
                  .sort((a, b) => Math.abs(Number(b.brokerProfit || 0)) - Math.abs(Number(a.brokerProfit || 0)))
                  .slice(0, 15);
                if (top.length === 0) return null;
                return (
                  <div style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '12px' }}>
                      💰 심볼별 브로커 수익 TOP 15
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={top} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="symbol" stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                        <YAxis stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
                          formatter={(v: any) => [`$${Number(v).toFixed(2)}`, '브로커 수익']}
                        />
                        <Bar dataKey="brokerProfit" radius={[4, 4, 0, 0]}>
                          {top.map((e: any, i: number) => (
                            <Cell key={i} fill={Number(e.brokerProfit || 0) >= 0 ? '#2ed573' : '#ff6b6b'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* 그룹별 테이블 */}
              <div style={s.tableWrap}>
                {(!symbolStats.groups || symbolStats.groups.length === 0) ? (
                  <div style={s.empty}>데이터가 없습니다</div>
                ) : (
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>심볼</th>
                        <th style={s.th}>전체</th>
                        <th style={s.th}>예약중</th>
                        <th style={s.th}>체결됨</th>
                        <th style={s.th}>취소됨</th>
                        <th style={s.th}>청산완료</th>
                        <th style={s.th}>⏰시간강제</th>
                        <th style={s.th}>⚡포인트강제</th>
                        <th style={s.th}>체결률</th>
                        <th style={s.th}>평균 랏</th>
                        <th style={s.th}>승률</th>
                        <th style={s.th}>고객 손익</th>
                        <th style={s.th}>마스터 손익</th>
                        <th style={s.th}>Agent 비용</th>
                        <th style={s.th}>브로커 수익</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(symbolStats.groups || []).map((g: any) => {
                        const isCollapsed = collapsedGroups.has(g.groupName);
                        const sub = g.subtotal || {};
                        return (
                          <React.Fragment key={g.groupName}>
                            {/* 그룹 헤더 */}
                            <tr
                              style={{ background: 'rgba(102,126,234,0.12)', cursor: 'pointer', fontWeight: 600 }}
                              onClick={() => {
                                const next = new Set(collapsedGroups);
                                if (next.has(g.groupName)) next.delete(g.groupName);
                                else next.add(g.groupName);
                                setCollapsedGroups(next);
                              }}
                            >
                              <td style={{ ...s.td, color: '#667eea' }}>
                                {isCollapsed ? '▶' : '▼'} 📂 {g.groupName} ({(g.symbols || []).length}개)
                              </td>
                              <td style={s.td}><span style={s.mono}>{sub.totalPositions ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{sub.pendingCount ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{sub.filledCount ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{sub.cancelledCount ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{sub.closedCount ?? 0}</span></td>

                              <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{sub.forcedByTimeCount ?? 0}</span></td>

                              <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{sub.forcedByAdverseCount ?? 0}</span></td>
                              <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{sub.filledRate || '-'}</span></td>
                              <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                              <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                              <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(sub.customerProfit || 0)) }}>{fmt(sub.customerProfit || 0)}</span></td>
                              <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(sub.masterProfit || 0)) }}>{fmt(sub.masterProfit || 0)}</span></td>
                              <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(sub.agentCost || 0)))}</span></td>
                              <td style={s.td}><span style={{ color: pnlColor(Number(sub.brokerProfit || 0)), fontFamily: 'monospace', fontWeight: 700 }}>{fmt(sub.brokerProfit || 0)}</span></td>
                            </tr>

                            {/* 그룹 내 심볼들 */}
                            {!isCollapsed && (g.symbols || []).map((sym: any, idx: number) => (
                              <tr key={sym.symbol} style={idx % 2 === 0 ? s.trEven : s.trOdd}>
                                <td style={{ ...s.td, paddingLeft: '32px' }}>
                                  <span style={{ color: '#667eea', fontFamily: 'monospace', fontWeight: 600 }}>{sym.symbol}</span>
                                </td>
                                <td style={s.td}><span style={s.mono}>{sym.totalPositions}</span></td>
                                <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{sym.pendingCount}</span></td>
                                <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{sym.filledCount}</span></td>
                                <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{sym.cancelledCount}</span></td>
                                <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{sym.closedCount}</span></td>

                                <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{sym.forcedByTimeCount}</span></td>

                                <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{sym.forcedByAdverseCount}</span></td>
                                <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{sym.filledRate}</span></td>
                                <td style={s.td}><span style={{ color: '#fff', fontFamily: 'monospace', fontSize: '12px' }}>{sym.avgLots != null ? Number(sym.avgLots).toFixed(2) : '-'}</span></td>
                                <td style={s.td}><span style={{ color: winRateColor(sym.winRate), fontFamily: 'monospace', fontWeight: 600 }}>{sym.winRate || '-'}</span></td>
                                <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(sym.customerProfit || 0)) }}>{fmt(sym.customerProfit || 0)}</span></td>
                                <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(sym.masterProfit || 0)) }}>{fmt(sym.masterProfit || 0)}</span></td>
                                <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(sym.agentCost || 0)))}</span></td>
                                <td style={s.td}><span style={{ color: pnlColor(Number(sym.brokerProfit || 0)), fontFamily: 'monospace', fontWeight: 700 }}>{fmt(sym.brokerProfit || 0)}</span></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}

                      {/* 전체 합계 */}
                      {symbolStats.total && (
                        <tr style={{ background: 'rgba(247,151,30,0.15)', fontWeight: 700, borderTop: '2px solid rgba(247,151,30,0.5)' }}>
                          <td style={{ ...s.td, color: '#f7971e', fontSize: '13px' }}>🏆 전체 합계</td>
                          <td style={s.td}><span style={s.mono}>{symbolStats.total.totalPositions ?? 0}</span></td>
                          <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{symbolStats.total.pendingCount ?? 0}</span></td>
                          <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{symbolStats.total.filledCount ?? 0}</span></td>
                          <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{symbolStats.total.cancelledCount ?? 0}</span></td>
                          <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{symbolStats.total.closedCount ?? 0}</span></td>

                          <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{symbolStats.total.forcedByTimeCount ?? 0}</span></td>

                          <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{symbolStats.total.forcedByAdverseCount ?? 0}</span></td>
                          <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{symbolStats.total.filledRate || '-'}</span></td>
                          <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                          <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(symbolStats.total.customerProfit || 0)) }}>{fmt(symbolStats.total.customerProfit || 0)}</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(symbolStats.total.masterProfit || 0)) }}>{fmt(symbolStats.total.masterProfit || 0)}</span></td>
                          <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(symbolStats.total.agentCost || 0)))}</span></td>
                          <td style={s.td}><span style={{ color: pnlColor(Number(symbolStats.total.brokerProfit || 0)), fontFamily: 'monospace', fontWeight: 700, fontSize: '14px' }}>{fmt(symbolStats.total.brokerProfit || 0)}</span></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* ── 서브탭: 일별 ── */}
          {statsSubTab === 'daily' && (() => {
            const rows = dailyStats.rows || [];
            // 차트용: 오래된 날짜가 왼쪽이 자연스러움 (역순)
            const chartData = [...rows].reverse();
            return (
              <div>
                {/* 라인 차트 */}
                {chartData.length > 0 && (
                  <div style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '12px' }}>
                      📈 일별 브로커 수익 추이
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="period" stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={50} />
                        <YAxis stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
                          formatter={(v: any, name: any) => [`$${Number(v).toFixed(2)}`, String(name || '')]}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line type="monotone" dataKey="brokerProfit" name="브로커 수익" stroke="#f7971e" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="customerProfit" name="고객 손익" stroke="#667eea" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* 거래수 바 차트 */}
                {chartData.length > 0 && (
                  <div style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '12px' }}>
                      📊 일별 거래수
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="period" stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={50} />
                        <YAxis stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar dataKey="filledCount" name="체결" stackId="a" fill="#2ed573" />
                        <Bar dataKey="closedCount" name="청산" stackId="a" fill="#667eea" />
                        <Bar dataKey="cancelledCount" name="취소" stackId="a" fill="#6c757d" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* 테이블 */}
                <div style={s.tableWrap}>
                  {rows.length === 0 ? <div style={s.empty}>데이터가 없습니다</div> : (
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>날짜</th>
                          <th style={s.th}>전체</th>
                          <th style={s.th}>예약중</th>
                          <th style={s.th}>체결됨</th>
                          <th style={s.th}>취소됨</th>
                          <th style={s.th}>청산완료</th>
                          <th style={s.th}>⏰시간강제</th>
                          <th style={s.th}>⚡포인트강제</th>
                          <th style={s.th}>체결률</th>
                          <th style={s.th}>평균 랏</th>
                          <th style={s.th}>승률</th>
                          <th style={s.th}>고객 손익</th>
                          <th style={s.th}>마스터 손익</th>
                          <th style={s.th}>Agent 비용</th>
                          <th style={s.th}>브로커 수익</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r: any, idx: number) => (
                          <tr key={r.period} style={idx % 2 === 0 ? s.trEven : s.trOdd}>
                            <td style={s.td}><span style={{ ...s.mono, color: '#f7971e', fontWeight: 600 }}>{r.period}</span></td>
                            <td style={s.td}><span style={s.mono}>{r.totalPositions}</span></td>
                            <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{r.pendingCount}</span></td>
                            <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{r.filledCount}</span></td>
                            <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{r.cancelledCount}</span></td>
                            <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{r.closedCount}</span></td>

                            <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{r.forcedByTimeCount}</span></td>

                            <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{r.forcedByAdverseCount}</span></td>
                            <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{r.filledRate}</span></td>
                            <td style={s.td}><span style={{ color: '#fff', fontFamily: 'monospace', fontSize: '12px' }}>{r.avgLots != null ? Number(r.avgLots).toFixed(2) : '-'}</span></td>
                            <td style={s.td}><span style={{ color: winRateColor(r.winRate), fontFamily: 'monospace', fontWeight: 600 }}>{r.winRate || '-'}</span></td>
                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(r.customerProfit || 0)) }}>{fmt(r.customerProfit || 0)}</span></td>
                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(r.masterProfit || 0)) }}>{fmt(r.masterProfit || 0)}</span></td>
                            <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(r.agentCost || 0)))}</span></td>
                            <td style={s.td}><span style={{ color: pnlColor(Number(r.brokerProfit || 0)), fontFamily: 'monospace', fontWeight: 700 }}>{fmt(r.brokerProfit || 0)}</span></td>
                          </tr>
                        ))}
                        {/* 전체 합계 */}
                        {dailyStats.total && (
                          <tr style={{ background: 'rgba(247,151,30,0.15)', fontWeight: 700, borderTop: '2px solid rgba(247,151,30,0.5)' }}>
                            <td style={{ ...s.td, color: '#f7971e', fontSize: '13px' }}>🏆 전체 합계</td>
                            <td style={s.td}><span style={s.mono}>{dailyStats.total.totalPositions ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{dailyStats.total.pendingCount ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{dailyStats.total.filledCount ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{dailyStats.total.cancelledCount ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{dailyStats.total.closedCount ?? 0}</span></td>

                            <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{dailyStats.total.forcedByTimeCount ?? 0}</span></td>

                            <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{dailyStats.total.forcedByAdverseCount ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{dailyStats.total.filledRate || '-'}</span></td>
                            <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                            <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(dailyStats.total.customerProfit || 0)) }}>{fmt(dailyStats.total.customerProfit || 0)}</span></td>
                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(dailyStats.total.masterProfit || 0)) }}>{fmt(dailyStats.total.masterProfit || 0)}</span></td>
                            <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(dailyStats.total.agentCost || 0)))}</span></td>
                            <td style={s.td}><span style={{ color: pnlColor(Number(dailyStats.total.brokerProfit || 0)), fontFamily: 'monospace', fontWeight: 700, fontSize: '14px' }}>{fmt(dailyStats.total.brokerProfit || 0)}</span></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── 서브탭: 월별 ── */}
          {statsSubTab === 'monthly' && (() => {
            const rows = monthlyStats.rows || [];
            // 차트용: 오래된 월부터 (왼쪽)
            const chartData = [...rows].reverse();
            return (
              <div>
                {/* 바 차트 - 월별 브로커 수익 */}
                {chartData.length > 0 && (
                  <div style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '12px' }}>
                      💰 월별 브로커 수익
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="period" stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 12 }} />
                        <YAxis stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
                          formatter={(v: any) => [`$${Number(v).toFixed(2)}`, '브로커 수익']}
                        />
                        <Bar dataKey="brokerProfit" radius={[4, 4, 0, 0]}>
                          {chartData.map((e: any, i: number) => (
                            <Cell key={i} fill={Number(e.brokerProfit || 0) >= 0 ? '#2ed573' : '#ff6b6b'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* 손익 비교 라인 차트 (고객 vs 마스터 vs 브로커) */}
                {chartData.length > 0 && (
                  <div style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '12px' }}>
                      📈 월별 손익 비교
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="period" stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 12 }} />
                        <YAxis stroke="rgba(255,255,255,0.6)" tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
                          formatter={(v: any, name: any) => [`$${Number(v).toFixed(2)}`, String(name || '')]}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line type="monotone" dataKey="customerProfit" name="고객 손익" stroke="#667eea" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="masterProfit" name="마스터 손익" stroke="#f7971e" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="brokerProfit" name="브로커 수익" stroke="#2ed573" strokeWidth={3} dot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* 테이블 */}
                <div style={s.tableWrap}>
                  {rows.length === 0 ? <div style={s.empty}>데이터가 없습니다</div> : (
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>월</th>
                          <th style={s.th}>전체</th>
                          <th style={s.th}>예약중</th>
                          <th style={s.th}>체결됨</th>
                          <th style={s.th}>취소됨</th>
                          <th style={s.th}>청산완료</th>
                          <th style={s.th}>⏰시간강제</th>
                          <th style={s.th}>⚡포인트강제</th>
                          <th style={s.th}>체결률</th>
                          <th style={s.th}>평균 랏</th>
                          <th style={s.th}>승률</th>
                          <th style={s.th}>일평균 수익</th>
                          <th style={s.th}>고객 손익</th>
                          <th style={s.th}>마스터 손익</th>
                          <th style={s.th}>Agent 비용</th>
                          <th style={s.th}>브로커 수익</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r: any, idx: number) => {
                          // 해당 월의 일 수 계산 (월말 기준)
                          const [y, m] = String(r.period).split('-').map(Number);
                          const daysInMonth = new Date(y, m, 0).getDate();
                          // 이번 달이면 오늘까지만
                          const now = new Date();
                          const isCurrentMonth = now.getFullYear() === y && (now.getMonth() + 1) === m;
                          const effectiveDays = isCurrentMonth ? now.getDate() : daysInMonth;
                          const daily = Number(r.brokerProfit || 0) / Math.max(1, effectiveDays);
                          return (
                            <tr key={r.period} style={idx % 2 === 0 ? s.trEven : s.trOdd}>
                              <td style={s.td}><span style={{ ...s.mono, color: '#f7971e', fontWeight: 600 }}>{r.period}</span></td>
                              <td style={s.td}><span style={s.mono}>{r.totalPositions}</span></td>
                              <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{r.pendingCount}</span></td>
                              <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{r.filledCount}</span></td>
                              <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{r.cancelledCount}</span></td>
                              <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{r.closedCount}</span></td>

                              <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{r.forcedByTimeCount}</span></td>

                              <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{r.forcedByAdverseCount}</span></td>
                              <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{r.filledRate}</span></td>
                              <td style={s.td}><span style={{ color: '#fff', fontFamily: 'monospace', fontSize: '12px' }}>{r.avgLots != null ? Number(r.avgLots).toFixed(2) : '-'}</span></td>
                              <td style={s.td}><span style={{ color: winRateColor(r.winRate), fontFamily: 'monospace', fontWeight: 600 }}>{r.winRate || '-'}</span></td>
                              <td style={s.td}><span style={{ ...s.mono, color: pnlColor(daily), fontSize: '12px' }}>{fmt(daily)}</span></td>
                              <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(r.customerProfit || 0)) }}>{fmt(r.customerProfit || 0)}</span></td>
                              <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(r.masterProfit || 0)) }}>{fmt(r.masterProfit || 0)}</span></td>
                              <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(r.agentCost || 0)))}</span></td>
                              <td style={s.td}><span style={{ color: pnlColor(Number(r.brokerProfit || 0)), fontFamily: 'monospace', fontWeight: 700 }}>{fmt(r.brokerProfit || 0)}</span></td>
                            </tr>
                          );
                        })}
                        {/* 전체 합계 */}
                        {monthlyStats.total && (
                          <tr style={{ background: 'rgba(247,151,30,0.15)', fontWeight: 700, borderTop: '2px solid rgba(247,151,30,0.5)' }}>
                            <td style={{ ...s.td, color: '#f7971e', fontSize: '13px' }}>🏆 전체 합계</td>
                            <td style={s.td}><span style={s.mono}>{monthlyStats.total.totalPositions ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{monthlyStats.total.pendingCount ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: '#2ed573', fontFamily: 'monospace' }}>{monthlyStats.total.filledCount ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{monthlyStats.total.cancelledCount ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: '#667eea', fontFamily: 'monospace' }}>{monthlyStats.total.closedCount ?? 0}</span></td>

                            <td style={s.td}><span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{monthlyStats.total.forcedByTimeCount ?? 0}</span></td>

                            <td style={s.td}><span style={{ color: '#ef4444', fontFamily: 'monospace' }}>{monthlyStats.total.forcedByAdverseCount ?? 0}</span></td>
                            <td style={s.td}><span style={{ color: '#f7971e', fontFamily: 'monospace' }}>{monthlyStats.total.filledRate || '-'}</span></td>
                            <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                            <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                            <td style={s.td}><span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span></td>
                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(monthlyStats.total.customerProfit || 0)) }}>{fmt(monthlyStats.total.customerProfit || 0)}</span></td>
                            <td style={s.td}><span style={{ ...s.mono, color: pnlColor(Number(monthlyStats.total.masterProfit || 0)) }}>{fmt(monthlyStats.total.masterProfit || 0)}</span></td>
                            <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{fmt(-Math.abs(Number(monthlyStats.total.agentCost || 0)))}</span></td>
                            <td style={s.td}><span style={{ color: pnlColor(Number(monthlyStats.total.brokerProfit || 0)), fontFamily: 'monospace', fontWeight: 700, fontSize: '14px' }}>{fmt(monthlyStats.total.brokerProfit || 0)}</span></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── 룰 생성/수정 모달 ── */}
      {showRuleModal && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={s.modalTitle}>{editingRule ? '룰 수정' : '룰 생성'}</h3>

            {/* 기본 정보 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div style={s.formGroup}>
                <label style={s.label}>룰 이름 *</label>
                <input style={s.input} value={ruleForm.name}
                  onChange={e => setRuleForm({ ...ruleForm, name: e.target.value })}
                  placeholder="예: 기본 포인트 룰" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>마스터 계좌 *</label>
                <input style={s.input} value={ruleForm.masterAccountId}
                  onChange={e => setRuleForm({ ...ruleForm, masterAccountId: e.target.value })}
                  placeholder="예: 102907" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>기본 포인트 (미설정 심볼 적용)</label>
                <input style={s.input} type="number" value={ruleForm.defaultPoint}
                  onChange={e => setRuleForm({ ...ruleForm, defaultPoint: parseInt(e.target.value) || 100 })}
                  placeholder="100" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>설명</label>
                <input style={s.input} value={ruleForm.description}
                  onChange={e => setRuleForm({ ...ruleForm, description: e.target.value })}
                  placeholder="룰 설명 (선택)" />
              </div>
            </div>

            {/* 강제 진입 설정 */}
            <div style={{
              marginBottom: '20px',
              padding: '14px',
              background: ruleForm.forceEntryEnabled ? 'rgba(46,213,115,0.08)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${ruleForm.forceEntryEnabled ? 'rgba(46,213,115,0.3)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: '8px',
              transition: 'all 0.2s',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: ruleForm.forceEntryEnabled ? '14px' : 0 }}>
                <input
                  type="checkbox"
                  checked={ruleForm.forceEntryEnabled}
                  onChange={e => setRuleForm({ ...ruleForm, forceEntryEnabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: 600, color: ruleForm.forceEntryEnabled ? '#2ed573' : 'rgba(255,255,255,0.7)' }}>
                  ⚡ 강제 진입 (Force Entry) 활성화
                </span>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                  {ruleForm.forceEntryEnabled ? '시간 초과 또는 포인트 강제진입 도달 시 자동 진입' : 'OFF — Limit Order만 사용'}
                </span>
              </label>
              {ruleForm.forceEntryEnabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={s.formGroup}>
                    <label style={s.label}>
                      기본 시간 초과 (초)
                      <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400, marginLeft: '6px', fontSize: '11px' }}>
                        진입 후 N초 경과 시 강제 진입
                      </span>
                    </label>
                    <input style={s.input} type="number" min="1" value={ruleForm.defaultTimeoutSeconds}
                      onChange={e => setRuleForm({ ...ruleForm, defaultTimeoutSeconds: parseInt(e.target.value) || 300 })}
                      placeholder="300" />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>
                      기본 포인트 강제진입
                      <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400, marginLeft: '6px', fontSize: '11px' }}>
                        고객 유리 방향 N포인트 도달 시 강제 진입
                      </span>
                    </label>
                    <input style={s.input} type="number" min="1" value={ruleForm.defaultAdversePoint}
                      onChange={e => setRuleForm({ ...ruleForm, defaultAdversePoint: parseInt(e.target.value) || 50 })}
                      placeholder="50" />
                  </div>
                </div>
              )}
            </div>

            {/* 심볼별 포인트 설정 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                  심볼별 포인트 설정 <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(비워두면 기본 포인트 적용)</span>
                </h4>
              </div>
              {/* 필터 */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <input style={{ ...s.input, maxWidth: '200px', padding: '6px 12px', fontSize: '12px' }}
                  placeholder="🔍 심볼 검색"
                  value={symbolSearch}
                  onChange={e => setSymbolSearch(e.target.value)} />
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {groups.slice(0, 8).map(g => (
                    <button key={g}
                      style={symbolGroupFilter === g
                        ? { ...s.groupBtn, background: 'rgba(102,126,234,0.3)', color: '#667eea', border: '1px solid rgba(102,126,234,0.5)' }
                        : s.groupBtn}
                      onClick={() => setSymbolGroupFilter(g)}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              {/* 심볼 테이블 */}
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px' }}>
                <table style={{ ...s.table, fontSize: '12px' }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: '#1a1a2e', zIndex: 1 }}>
                      <th style={{ ...s.th, fontSize: '11px' }}>심볼</th>
                      <th style={{ ...s.th, fontSize: '11px' }}>설명</th>
                      <th style={{ ...s.th, fontSize: '11px' }}>그룹</th>
                      <th style={{ ...s.th, fontSize: '11px', width: '100px' }}>포인트</th>
                      {ruleForm.forceEntryEnabled && (
                        <>
                          <th style={{ ...s.th, fontSize: '11px', width: '100px', color: '#2ed573' }}>⏱ 시간(초)</th>
                          <th style={{ ...s.th, fontSize: '11px', width: '100px', color: '#2ed573' }}>⚡ 포인트강제pt</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSymbols.map((sym, idx) => (
                      <tr key={sym.symbol} style={idx % 2 === 0 ? s.trEven : s.trOdd}>
                        <td style={{ ...s.td, padding: '6px 10px' }}>
                          <span style={{ color: '#667eea', fontFamily: 'monospace', fontWeight: 700, fontSize: '12px' }}>{sym.symbol}</span>
                        </td>
                        <td style={{ ...s.td, padding: '6px 10px', color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>{sym.description}</td>
                        <td style={{ ...s.td, padding: '6px 10px', color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>{sym.groupName}</td>
                        <td style={{ ...s.td, padding: '4px 10px' }}>
                          <input
                            style={{ width: '80px', padding: '4px 8px', background: symbolPoints[sym.symbol] !== undefined && symbolPoints[sym.symbol] !== '' ? 'rgba(247,151,30,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${symbolPoints[sym.symbol] !== undefined && symbolPoints[sym.symbol] !== '' ? 'rgba(247,151,30,0.4)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none', textAlign: 'right' }}
                            type="number"
                            placeholder={String(ruleForm.defaultPoint)}
                            value={symbolPoints[sym.symbol] !== undefined ? symbolPoints[sym.symbol] : ''}
                            onChange={e => {
                              const val = e.target.value;
                              setSymbolPoints(prev => ({
                                ...prev,
                                [sym.symbol]: val === '' ? '' : parseInt(val)
                              }));
                            }}
                          />
                        </td>
                        {ruleForm.forceEntryEnabled && (
                          <>
                            <td style={{ ...s.td, padding: '4px 10px' }}>
                              <input
                                style={{ width: '80px', padding: '4px 8px', background: symbolTimeouts[sym.symbol] !== undefined && symbolTimeouts[sym.symbol] !== '' ? 'rgba(46,213,115,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${symbolTimeouts[sym.symbol] !== undefined && symbolTimeouts[sym.symbol] !== '' ? 'rgba(46,213,115,0.4)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none', textAlign: 'right' }}
                                type="number"
                                placeholder={String(ruleForm.defaultTimeoutSeconds)}
                                value={symbolTimeouts[sym.symbol] !== undefined ? symbolTimeouts[sym.symbol] : ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  setSymbolTimeouts(prev => ({
                                    ...prev,
                                    [sym.symbol]: val === '' ? '' : parseInt(val)
                                  }));
                                }}
                              />
                            </td>
                            <td style={{ ...s.td, padding: '4px 10px' }}>
                              <input
                                style={{ width: '80px', padding: '4px 8px', background: symbolAdversePoints[sym.symbol] !== undefined && symbolAdversePoints[sym.symbol] !== '' ? 'rgba(46,213,115,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${symbolAdversePoints[sym.symbol] !== undefined && symbolAdversePoints[sym.symbol] !== '' ? 'rgba(46,213,115,0.4)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none', textAlign: 'right' }}
                                type="number"
                                placeholder={String(ruleForm.defaultAdversePoint)}
                                value={symbolAdversePoints[sym.symbol] !== undefined ? symbolAdversePoints[sym.symbol] : ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  setSymbolAdversePoints(prev => ({
                                    ...prev,
                                    [sym.symbol]: val === '' ? '' : parseInt(val)
                                  }));
                                }}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button style={s.delBtn} onClick={() => setShowRuleModal(false)}>취소</button>
              <button style={s.saveBtn} onClick={saveRule}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 계좌 등록 모달 ── */}
      {showAccountModal && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={s.modalTitle}>전환 포인트 계좌 등록</h3>

            {/* 룰 선택 */}
            <div style={s.formGroup}>
              <label style={s.label}>적용 룰 *</label>
              <select style={s.select} value={newAccount.ruleId}
                onChange={e => setNewAccount({ ...newAccount, ruleId: e.target.value })}>
                <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>-- 룰 선택 --</option>
                {rules.map(r => (
                  <option key={r.id} value={r.id} style={{ background: '#1a1a2e', color: '#fff' }}>{r.name} (기본 {r.defaultPoint}pt)</option>
                ))}
              </select>
            </div>

            {/* 그룹별 계좌 검색 */}
            <div style={s.formGroup}>
              <label style={s.label}>그룹별 계좌 검색</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select style={{ ...s.select, flex: 1 }}
                  value={selectedGroup}
                  onChange={e => setSelectedGroup(e.target.value)}>
                  <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>-- 그룹 선택 --</option>
                  {groupList.map((g: string) => (
                    <option key={g} value={g} style={{ background: '#1a1a2e', color: '#fff' }}>{g}</option>
                  ))}
                </select>
                <button style={{ ...s.saveBtn, whiteSpace: 'nowrap' as const, padding: '8px 16px' }}
                  onClick={searchGroupAccounts}
                  disabled={groupLoading || !selectedGroup}>
                  {groupLoading ? '조회중...' : '검색'}
                </button>
              </div>
            </div>

            {/* 계좌 체크박스 리스트 */}
            {groupAccounts.length > 0 && (
              <div style={s.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={s.label}>계좌 선택 ({checkedIds.size}/{groupAccounts.length})</label>
                  <button style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', padding: '3px 10px', color: 'rgba(255,255,255,0.6)', fontSize: '12px', cursor: 'pointer' }}
                    onClick={toggleAll}>
                    {checkedIds.size === groupAccounts.length ? '전체 해제' : '전체 선택'}
                  </button>
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                  {groupAccounts.map((acc: any) => (
                    <div key={acc.login}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', background: checkedIds.has(String(acc.login)) ? 'rgba(102,126,234,0.1)' : 'transparent' }}
                      onClick={() => toggleCheck(String(acc.login))}>
                      <input type="checkbox" checked={checkedIds.has(String(acc.login))} onChange={() => toggleCheck(String(acc.login))} style={{ cursor: 'pointer' }} />
                      <span style={{ fontFamily: 'monospace', color: '#667eea', fontSize: '13px', fontWeight: 700 }}>{acc.login}</span>
                      <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{acc.name}</span>
                      <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginLeft: 'auto' }}>{acc.group}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 직접 입력 */}
            <div style={s.formGroup}>
              <label style={s.label}>직접 입력 (계좌번호)</label>
              <input style={s.input} value={newAccount.accountId}
                onChange={e => setNewAccount({ ...newAccount, accountId: e.target.value })}
                placeholder="예: 102929 (그룹 선택 없이 직접 입력)" />
            </div>

            {registerResult && (
              <div style={{ padding: '10px', background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: '8px', color: '#ff6b6b', fontSize: '13px', marginBottom: '12px' }}>
                {registerResult}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button style={s.delBtn} onClick={() => {
                setShowAccountModal(false);
                setCheckedIds(new Set());
                setGroupAccounts([]);
                setRegisterResult('');
              }}>취소</button>
              <button style={{ ...s.saveBtn, opacity: registerLoading ? 0.6 : 1, padding: '6px 16px' }}
                onClick={addAccount}
                disabled={registerLoading}>
                {registerLoading ? '등록 중...' : `등록${checkedIds.size > 0 ? ` (${checkedIds.size}개)` : newAccount.accountId ? ' (1개)' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { color: '#fff', width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, margin: '0 0 4px' },
  sub: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
  tab: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 18px', color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer' },
  tabActive: { background: 'rgba(102,126,234,0.2)', border: '1px solid rgba(102,126,234,0.4)', color: '#667eea', fontWeight: 600 },
  addBtn: { background: 'rgba(102,126,234,0.2)', border: '1px solid rgba(102,126,234,0.4)', borderRadius: '8px', padding: '8px 18px', color: '#667eea', fontSize: '13px', cursor: 'pointer', fontWeight: 600 },
  tableWrap: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'auto', marginBottom: '24px' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { padding: '12px 14px', textAlign: 'left' as const, fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap' as const },
  trEven: { background: 'transparent' },
  trOdd: { background: 'rgba(255,255,255,0.02)' },
  td: { padding: '12px 14px', fontSize: '13px', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' as const },
  accId: { fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: '#667eea' },
  mono: { fontFamily: 'monospace', fontSize: '13px' },
  editBtn: { background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.25)', borderRadius: '6px', padding: '4px 10px', color: '#667eea', fontSize: '12px', cursor: 'pointer' },
  saveBtn: { background: 'rgba(46,213,115,0.15)', border: '1px solid rgba(46,213,115,0.3)', borderRadius: '6px', padding: '6px 16px', color: '#2ed573', fontSize: '13px', cursor: 'pointer' },
  delBtn: { background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: '6px', padding: '4px 10px', color: '#ff6b6b', fontSize: '12px', cursor: 'pointer' },
  groupBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '4px 10px', color: 'rgba(255,255,255,0.5)', fontSize: '11px', cursor: 'pointer' },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  empty: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  detailPanel: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px', marginTop: '24px' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  detailTitle: { fontSize: '18px', fontWeight: 700, margin: 0 },
  closeBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '4px 12px', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '28px', width: '420px' },
  modalTitle: { fontSize: '18px', fontWeight: 700, margin: '0 0 20px', color: '#fff' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' as const },
  select: { width: '100%', padding: '10px 14px', background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' as const, cursor: 'pointer', appearance: 'none' as const, colorScheme: 'dark' as const },
};

export default PointRulePage;
